// frontend/src/features/campaign/components/create/CreateCampaign.js
import React, { useState, useMemo, useRef } from 'react';
import axios from '../../../../api/axios';
import WizardStepper from './WizardStepper';
import WizardFooter from './WizardFooter';
import Step1BriefGoals from './Step1BriefGoals';
import Step2TargetCohort from './Step2TargetCohort';
import Step3OfferMatrix from './Step3OfferMatrix';
import Step4TeamAssign from './Step4TeamAssign';
import Step5TasksBudget from './Step5TasksBudget';
import Step6Governance from './Step6Governance';
import ImportPipelineLeadsModal from './ImportPipelineLeadsModal';
import AiGeneratePromptModal from './AiGeneratePromptModal';
import AiInterruptWarningModal from './AiInterruptWarningModal';
import UnsavedChangesReminderModal from './UnsavedChangesReminderModal';
import CampaignOutreachLauncherModal from './CampaignOutreachLauncherModal';
import GovernanceApprovalRequiredBanner, {
  RequestChangesModal,
  MemberSubmitChangeRequestModal,
  ExecutiveSignContractModal,
} from '../common/GovernanceApprovalRequiredBanner';
import {
  CAMPAIGN_TYPES,
  INDUSTRY_SECTORS,
  TARGET_REGIONS,
  AVAILABLE_PERSONAS,
  normalizePersonaName,
  ALL_CHANNELS,
  GOVERNANCE_GATES,
  PRESET_PRODUCTS,
} from '../../constants/campaignConstants';
import { SAMPLE_PIPELINE_LEADS, BLUEPRINT_PLAYBOOKS } from '../../data/initialCampaignData';
import { formatLocalDate, addDays, calculateDiffDays } from '../../utils/dateUtils';
import { calculateCampaignKPIs, parseCurrencyNumber } from '../../utils/campaignKpiCalculator';
import { computeCohortReachability } from '../../utils/campaignReachabilityUtils';
import { checkPauseApprovalRequirement, normalizeLead } from '../../utils/campaignUtils';
import {
  DEFAULT_TIMING_CONFIG,
  alignJourneyToTimingGuardrails,
} from '../../utils/campaignTimingUtils';
import '../CreateCampaign.css';

const getDynamicReachability = (importedLeads, fallbackAudience = 0) => {
  if (Array.isArray(importedLeads) && importedLeads.length > 0) {
    const stats = computeCohortReachability(importedLeads);
    return stats.anyChannelCount;
  }
  return Math.round(fallbackAudience * 0.82);
};

// Re-exports for backwards compatibility
export {
  SAMPLE_PIPELINE_LEADS,
  BLUEPRINT_PLAYBOOKS,
  CAMPAIGN_TYPES,
  INDUSTRY_SECTORS,
  TARGET_REGIONS,
  AVAILABLE_PERSONAS,
  ALL_CHANNELS,
  GOVERNANCE_GATES,
  PRESET_PRODUCTS,
};

function buildInitialFormData(initialCampaign, initialPlaybook) {
  if (initialCampaign) {
    const existingPersonas =
      initialCampaign.targetPersonas && initialCampaign.targetPersonas.length > 0
        ? initialCampaign.targetPersonas
        : ['VP Sales'];

    const defaultMatrix = existingPersonas.map((p) => ({
      persona: p,
      painPoint: `Managing pipeline unpredictability and manual outbound sales research in ${initialCampaign.targetMarket || 'Malaysia'}.`,
      valueProp: `Automated AI lead generation driving high-intent qualified executive meetings for ${initialCampaign.product || 'AI Lead Engine'}.`,
      proofPoint: `Validated 3.5x pipeline acceleration with verified decision-maker direct dials.`,
      cta: `Book a 15-minute consultation to review targeted accounts.`,
      objection: `Handling competitor tools: We provide direct data verification and multichannel cadence execution.`,
    }));

    const defaultJourney = [
      {
        step: 1,
        day: 1,
        channel: (initialCampaign.channels && initialCampaign.channels[0]) || 'Email',
        name: 'Value Hook & Problem Intro',
        condition: 'Initial Touch',
      },
      {
        step: 2,
        day: 4,
        channel: (initialCampaign.channels && initialCampaign.channels[1]) || 'LinkedIn',
        name: 'Connection Request & Social Context',
        condition: 'If No Reply',
      },
      {
        step: 3,
        day: 8,
        channel: (initialCampaign.channels && initialCampaign.channels[0]) || 'Email',
        name: 'Case Study & Verified Proof Points',
        condition: 'If No Reply',
      },
      {
        step: 4,
        day: 14,
        channel: (initialCampaign.channels && initialCampaign.channels[1]) || 'LinkedIn',
        name: 'Executive Consultation Offer',
        condition: 'If No Reply',
      },
      {
        step: 5,
        day: 21,
        channel: (initialCampaign.channels && initialCampaign.channels[0]) || 'Email',
        name: 'Final Break-up & Industry Benchmark Report',
        condition: 'If No Reply',
      },
    ];

    const rawStrategyLeads = [
      ...(Array.isArray(initialCampaign.strategy?.importedLeads)
        ? initialCampaign.strategy.importedLeads
        : []),
      ...(Array.isArray(initialCampaign.strategy?.targetLeads)
        ? initialCampaign.strategy.targetLeads
        : []),
    ];
    const strategyMap = new Map();
    rawStrategyLeads.forEach((l) => {
      const id = String(l?.id || l?.shared_item_id || l?.prospect_id || l?.lead_id || '');
      if (id) strategyMap.set(id, l);
    });

    const baseLeads =
      initialCampaign.importedLeads && initialCampaign.importedLeads.length > 0
        ? initialCampaign.importedLeads
        : initialCampaign.targetLeads && initialCampaign.targetLeads.length > 0
          ? initialCampaign.targetLeads
          : rawStrategyLeads;

    const resolvedInitialLeads = baseLeads.map((lead, idx) => {
      const id = String(
        lead?.id || lead?.shared_item_id || lead?.prospect_id || lead?.lead_id || ''
      );
      const rich = strategyMap.get(id) || {};
      return normalizeLead(
        {
          ...rich,
          ...lead,
          name: lead?.name || rich?.name,
          company: lead?.company || rich?.company,
          email: lead?.email || rich?.email,
          phone: lead?.phone || rich?.phone,
          jobTitle: lead?.jobTitle || lead?.title || rich?.jobTitle || rich?.title,
          industry: lead?.industry || rich?.industry,
          country: lead?.country || rich?.country,
          stage: lead?.stage || rich?.stage,
          leadScore: lead?.leadScore ?? rich?.leadScore,
        },
        idx
      );
    });

    const audienceCount =
      initialCampaign.audienceCount ||
      (resolvedInitialLeads.length > 0 ? resolvedInitialLeads.length : 420);

    return {
      id: initialCampaign.id || initialCampaign._id || null,
      status: initialCampaign.status || 'DRAFT',
      title: initialCampaign.name || '',
      primaryObjective: initialCampaign.objective || '',
      campaignType: initialCampaign.campaignType || CAMPAIGN_TYPES[0],
      industrySector: initialCampaign.industrySector || INDUSTRY_SECTORS[0],
      targetRegion: initialCampaign.targetMarket || TARGET_REGIONS[0],
      currency: initialCampaign.currency || 'USD',
      allocatedBudget: initialCampaign.budget || '$ 25,000',
      startDate:
        initialCampaign.startDate ||
        initialCampaign.strategy?.startDate ||
        (initialCampaign.createdAt
          ? formatLocalDate(new Date(initialCampaign.createdAt))
          : formatLocalDate(new Date())),
      endDate:
        initialCampaign.endDate ||
        initialCampaign.strategy?.endDate ||
        addDays(
          initialCampaign.startDate ||
            initialCampaign.strategy?.startDate ||
            (initialCampaign.createdAt
              ? formatLocalDate(new Date(initialCampaign.createdAt))
              : formatLocalDate(new Date())),
          Number(initialCampaign.durationDays) || 45
        ),
      durationDays: Number(initialCampaign.durationDays) || 45,
      product: initialCampaign.product || '',
      productDescription: initialCampaign.productDescription || '',
      targetPersonas: existingPersonas,
      channels: initialCampaign.channels || ['Email', 'LinkedIn'],
      avgDealSize: initialCampaign.strategy?.kpiForecast?.avgDealSize || 35000,
      audienceUniverse: audienceCount * 12,
      geoMatchCount: Math.round(audienceCount * 7.5),
      industryMatchCount: Math.round(audienceCount * 4),
      personaMatchCount: Math.round(audienceCount * 1.3),
      exclusionsCount: 140,
      finalAudienceCount: audienceCount,
      priorityHigh: Math.round(audienceCount * 0.38),
      priorityMed: Math.round(audienceCount * 0.45),
      priorityLow: Math.round(audienceCount * 0.17),
      importedLeads: resolvedInitialLeads,
      positioning:
        initialCampaign.strategy?.positioning ||
        `Empower ${existingPersonas.join(' / ')} with ${initialCampaign.product || 'AI Outbound'}.`,
      painPoints:
        initialCampaign.strategy?.painPoints ||
        'Low outbound reply rates, manual SDR prospect research, and CRM data decay.',
      matrix:
        initialCampaign.strategy?.matrix && initialCampaign.strategy.matrix.length > 0
          ? initialCampaign.strategy.matrix
          : defaultMatrix,
      journey:
        initialCampaign.strategy?.journey && initialCampaign.strategy.journey.length > 0
          ? initialCampaign.strategy.journey
          : defaultJourney,
      schedule:
        initialCampaign.strategy?.schedule || initialCampaign.schedule || DEFAULT_TIMING_CONFIG,
      forecastScenario: initialCampaign.strategy?.forecastScenario || 'baseline',
      reviewerRole: 'Sales Manager',
      submissionNotes: initialCampaign.submissionNotes || '',
      assignedMembers:
        initialCampaign.strategy?.assignedMembers || initialCampaign.assignedMembers || [],
    };
  }

  if (initialPlaybook) {
    const updatedMatrix =
      initialPlaybook.matrix && initialPlaybook.matrix.length > 0
        ? initialPlaybook.matrix
        : (initialPlaybook.targetPersonas || []).map((p) => ({
            persona: p,
            painPoint: `Managing pipeline unpredictability and manual outbound sales research in ${initialPlaybook.targetRegion || 'target market'}.`,
            valueProp: `Automated AI lead generation driving high-intent qualified executive meetings for ${initialPlaybook.product || 'the solution'}.`,
            proofPoint: `Validated 3.5x pipeline acceleration with verified decision-maker direct dials.`,
            cta: `Book a 15-minute consultation to review targeted accounts.`,
            objection: `Handling competitor tools: We provide direct data verification and multichannel cadence execution.`,
          }));

    const defaultJourney =
      initialPlaybook.journey && initialPlaybook.journey.length > 0
        ? initialPlaybook.journey
        : [
            {
              step: 1,
              day: 1,
              channel: (initialPlaybook.channels && initialPlaybook.channels[0]) || 'Email',
              name: 'Value Hook & Problem Intro',
              condition: 'Initial Touch',
            },
            {
              step: 2,
              day: 4,
              channel: (initialPlaybook.channels && initialPlaybook.channels[1]) || 'LinkedIn',
              name: 'Connection Request & Social Context',
              condition: 'If No Reply',
            },
            {
              step: 3,
              day: 8,
              channel: (initialPlaybook.channels && initialPlaybook.channels[0]) || 'Email',
              name: 'Case Study & Verified Proof Points',
              condition: 'If No Reply',
            },
            {
              step: 4,
              day: 14,
              channel: (initialPlaybook.channels && initialPlaybook.channels[1]) || 'LinkedIn',
              name: 'Executive Consultation Offer',
              condition: 'If No Reply',
            },
            {
              step: 5,
              day: 21,
              channel: (initialPlaybook.channels && initialPlaybook.channels[0]) || 'Email',
              name: 'Final Break-up & Industry Benchmark Report',
              condition: 'If No Reply',
            },
          ];

    const finalAud = initialPlaybook.finalAudienceCount || 780;
    const audUniv = initialPlaybook.audienceUniverse || finalAud * 10;
    const startStr = initialPlaybook.startDate || formatLocalDate(new Date());
    const duration = parseInt(initialPlaybook.durationDays, 10) || 45;
    const endStr = initialPlaybook.endDate || addDays(startStr, duration);

    return {
      title: initialPlaybook.name,
      primaryObjective: initialPlaybook.primaryObjective,
      campaignType: initialPlaybook.campaignType || CAMPAIGN_TYPES[0],
      industrySector: initialPlaybook.industrySector || INDUSTRY_SECTORS[0],
      targetRegion: initialPlaybook.targetRegion || TARGET_REGIONS[0],
      currency: initialPlaybook.currency || 'USD',
      allocatedBudget: initialPlaybook.allocatedBudget || '$ 25,000',
      startDate: startStr,
      endDate: endStr,
      durationDays: duration,
      product: initialPlaybook.product || '',
      productDescription: initialPlaybook.productDescription || '',
      targetPersonas: initialPlaybook.targetPersonas || [],
      channels: initialPlaybook.channels || ['Email', 'LinkedIn'],
      avgDealSize: initialPlaybook.avgDealSize || 35000,
      audienceUniverse: audUniv,
      geoMatchCount: initialPlaybook.geoMatchCount || Math.round(audUniv * 0.67),
      industryMatchCount: initialPlaybook.industryMatchCount || Math.round(audUniv * 0.35),
      personaMatchCount: initialPlaybook.personaMatchCount || Math.round(audUniv * 0.11),
      exclusionsCount: initialPlaybook.exclusionsCount || 140,
      finalAudienceCount: finalAud,
      priorityHigh: initialPlaybook.priorityHigh || Math.round(finalAud * 0.38),
      priorityMed: initialPlaybook.priorityMed || Math.round(finalAud * 0.45),
      priorityLow: initialPlaybook.priorityLow || Math.round(finalAud * 0.17),
      importedLeads: initialPlaybook.importedLeads || [],
      positioning:
        initialPlaybook.positioning ||
        `Empower ${(initialPlaybook.targetPersonas || []).join(' / ')} with ${initialPlaybook.product || 'AI Outbound'} in ${initialPlaybook.targetRegion || 'target markets'}.`,
      painPoints:
        initialPlaybook.painPoints ||
        'Low outbound reply rates, manual SDR prospect research, and CRM data decay.',
      matrix: updatedMatrix,
      journey: defaultJourney,
      schedule:
        initialPlaybook.strategy?.schedule || initialPlaybook.schedule || DEFAULT_TIMING_CONFIG,
      forecastScenario: 'baseline',
      reviewerRole: 'Sales Manager',
      submissionNotes: initialPlaybook.submissionNotes || '',
      assignedMembers:
        initialPlaybook.strategy?.assignedMembers || initialPlaybook.assignedMembers || [],
    };
  }

  const defaultStart = formatLocalDate(new Date());
  const defaultDuration = 45;
  const defaultEnd = addDays(defaultStart, defaultDuration);

  return {
    title: '',
    primaryObjective: '',
    campaignType: '',
    industrySector: '',
    targetRegion: '',
    allocatedBudget: '',
    startDate: defaultStart,
    endDate: defaultEnd,
    durationDays: defaultDuration,
    product: '',
    productDescription: '',
    targetPersonas: [],
    channels: [],
    avgDealSize: '',
    audienceUniverse: 0,
    geoMatchCount: 0,
    industryMatchCount: 0,
    personaMatchCount: 0,
    exclusionsCount: 0,
    finalAudienceCount: 0,
    priorityHigh: 0,
    priorityMed: 0,
    priorityLow: 0,
    importedLeads: [],
    positioning: '',
    painPoints: '',
    matrix: [],
    journey: [],
    schedule: DEFAULT_TIMING_CONFIG,
    forecastScenario: 'baseline',
    reviewerRole: 'Sales Manager',
    submissionNotes: '',
    assignedMembers: [],
  };
}

function calculateLeadFingerprint(roles) {
  // Derive fingerprint from the strategic role set (e.g. ['CEO', 'VP Sales']).
  // Raw contact details (emails, phones, IDs) are bypassed so CRM auto-syncs don't trigger accidental unauthorized change flags.
  const cleanRoles = Array.isArray(roles) && roles.length > 0 ? roles : ['VP Sales'];
  return `ROLES:${cleanRoles
    .map((r) =>
      String(r || '')
        .trim()
        .toLowerCase()
    )
    .filter(Boolean)
    .sort()
    .join('|')}`;
}

export function checkRolesOutOfSync(tab2Roles, matrix) {
  const normTab2 = Array.from(
    new Set(
      (tab2Roles || [])
        .map((r) =>
          String(r || '')
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
    )
  ).sort();
  const normTab3 = Array.from(
    new Set(
      (matrix || [])
        .map((m) =>
          String(m.persona || '')
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
    )
  ).sort();

  if (normTab2.length === 0 && normTab3.length === 0) return false;
  if (normTab2.length !== normTab3.length) return true;
  return normTab2.some((r, idx) => r !== normTab3[idx]);
}

export default function CreateCampaign({
  workspaceName = 'Primary Workspace',
  userRoleLabel = 'Sales Member',
  currentUserName = 'Sales Strategist',
  canDirectApprove = false,
  initialPlaybook = null,
  initialCampaign = null,
  initialStep = 1,
  mode = 'CREATE',
  onCancel,
  onSaveCampaign,
  onCampaignDispatchedUpdate,
}) {
  const isExistingCampaign = Boolean(initialCampaign);
  const isEditMode = mode === 'EDIT' || isExistingCampaign;
  const [isEditing, setIsEditing] = useState(mode === 'CREATE' && !initialCampaign);
  const isReadOnly = isExistingCampaign ? !isEditing : false;
  const [wizardStep, setWizardStep] = useState(initialStep || 1);
  const [maxStepReached, setMaxStepReached] = useState(isEditMode ? 6 : 1);
  const [pendingTargetStep, setPendingTargetStep] = useState(3);

  // ── Form Data State ──
  const [formData, setFormData] = useState(() =>
    buildInitialFormData(initialCampaign, initialPlaybook)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const handleCancelEditing = () => {
    if (initialCampaign) {
      setFormData(buildInitialFormData(initialCampaign, initialPlaybook));
    }
    setIsEditing(false);
    setHasUnsavedChanges(false);
    setValidationError('');
  };

  // ── Lead Pipeline Import & Lusha Suggestion State ──
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isLushaModalOpen, setIsLushaModalOpen] = useState(false);
  const [isRequestChangesModalOpen, setIsRequestChangesModalOpen] = useState(false);
  const [isMemberChangeRequestModalOpen, setIsMemberChangeRequestModalOpen] = useState(false);
  const [isExecutiveSignModalOpen, setIsExecutiveSignModalOpen] = useState(false);
  const [isPauseApprovalModalOpen, setIsPauseApprovalModalOpen] = useState(false);
  const [isLoadingPipelineLeads, setIsLoadingPipelineLeads] = useState(false);
  const [pipelineLeads, setPipelineLeads] = useState([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [modalSearch, setModalSearch] = useState('');
  const [modalStageFilter, setModalStageFilter] = useState('ALL');
  const [modalScoreFilter, setModalScoreFilter] = useState('ALL');
  const [importedSearch, setImportedSearch] = useState('');
  const [selectedImportedIds, setSelectedImportedIds] = useState(new Set());

  // ── Sync with CRM Lead Deletions (Real-time) ──
  React.useEffect(() => {
    const handleLeadChanged = (e) => {
      const data = e?.detail;
      if (data?.action === 'deleted') {
        const deletedIds = new Set(
          [data?.leadId, ...(Array.isArray(data?.leadIdentifiers) ? data.leadIdentifiers : [])]
            .filter(Boolean)
            .map(String)
        );
        if (deletedIds.size === 0) return;

        setFormData((prev) => {
          const currentLeads = prev.importedLeads || [];
          const matches = (l) => {
            const ids = [l?.id, l?.shared_item_id, l?.lead_id, l?.leadId, l?.external_id]
              .filter(Boolean)
              .map(String);
            return ids.some((i) => deletedIds.has(i));
          };
          const filtered = currentLeads.filter((l) => !matches(l));
          if (filtered.length !== currentLeads.length) {
            return {
              ...prev,
              importedLeads: filtered,
              finalAudienceCount: filtered.length,
            };
          }
          return prev;
        });

        setPipelineLeads((prev) => {
          const matches = (l) => {
            const ids = [l?.id, l?.shared_item_id, l?.lead_id, l?.leadId, l?.external_id]
              .filter(Boolean)
              .map(String);
            return ids.some((i) => deletedIds.has(i));
          };
          return prev.filter((l) => !matches(l));
        });
      } else if (data?.action === 'updated') {
        const updatedIds = new Set(
          [data?.leadId, ...(Array.isArray(data?.leadIdentifiers) ? data.leadIdentifiers : [])]
            .filter(Boolean)
            .map(String)
        );
        if (updatedIds.size === 0) return;

        const newStage = data?.stage;
        if (!newStage) return;

        const isNewActive = String(newStage).toLowerCase() === 'new';

        const updateLeadObj = (l) => {
          const ids = [l?.id, l?.shared_item_id, l?.lead_id, l?.leadId, l?.external_id]
            .filter(Boolean)
            .map(String);
          if (ids.some((i) => updatedIds.has(i))) {
            return {
              ...l,
              stage: newStage,
              ...(isNewActive ? { leadResponse: 'no_reply' } : {}),
            };
          }
          return l;
        };

        setFormData((prev) => {
          const updatedImported = (prev.importedLeads || []).map(updateLeadObj);
          const updatedTarget = (prev.targetLeads || []).map(updateLeadObj);
          let updatedJourney = prev.journey;

          // If stage was reset to an active/new stage, clear stale responseType from journey steps
          if (isNewActive && Array.isArray(prev.journey)) {
            updatedJourney = prev.journey.map((step) => {
              if (!step?.leadResponses) return step;
              let stepChanged = false;
              const nextResponses = { ...step.leadResponses };
              Object.keys(nextResponses).forEach((k) => {
                if (updatedIds.has(String(k))) {
                  delete nextResponses[k];
                  stepChanged = true;
                }
              });
              return stepChanged ? { ...step, leadResponses: nextResponses } : step;
            });
          }

          return {
            ...prev,
            importedLeads: updatedImported,
            targetLeads: updatedTarget,
            journey: updatedJourney,
            strategy: prev.strategy
              ? {
                  ...prev.strategy,
                  importedLeads: (prev.strategy.importedLeads || []).map(updateLeadObj),
                  targetLeads: (prev.strategy.targetLeads || []).map(updateLeadObj),
                  journey: updatedJourney,
                }
              : prev.strategy,
          };
        });

        setPipelineLeads((prev) =>
          prev.map((l) => {
            const ids = [l?.id, l?.shared_item_id, l?.lead_id, l?.leadId, l?.external_id]
              .filter(Boolean)
              .map(String);
            if (ids.some((i) => updatedIds.has(i))) {
              return {
                ...l,
                stage: newStage,
              };
            }
            return l;
          })
        );
      }
    };
    window.addEventListener('lead-changed', handleLeadChanged);
    return () => window.removeEventListener('lead-changed', handleLeadChanged);
  }, []);

  // ── Step 1 AI Outreach Launcher Modal State ──
  const [outreachLauncherState, setOutreachLauncherState] = useState({
    isOpen: false,
    channel: 'Email',
    touchpoint: null,
    campaign: null,
    recipients: [],
    isPostCreation: false,
  });

  // ── Pause Approval Requirement Check (Role-based Direct Approval) ──
  const pauseCheck = useMemo(() => {
    return checkPauseApprovalRequirement({
      campaign: initialCampaign,
      currentUserName,
      userRoleLabel,
      canDirectApprove,
    });
  }, [initialCampaign, currentUserName, userRoleLabel, canDirectApprove]);

  // ── Parsed Revision Tags & Message for CHANGES_REQUESTED Status ──
  const parsedRevision = useMemo(() => {
    const raw = initialCampaign?.revisionFeedback || initialCampaign?.submissionNotes || '';
    const tagMatch = raw.match(/\[Tags?:\s*([^\]]+)\]/i);
    const tag = tagMatch ? tagMatch[1].trim() : null;
    const message = raw.replace(/\[Tags?:\s*[^\]]+\]/i, '').trim();
    return { tag, message };
  }, [initialCampaign?.revisionFeedback, initialCampaign?.submissionNotes]);

  // ── Step 6: Governance Engine & Digital Sign-Off State (AC-06) ──
  const isCampaignApprovedOrActive =
    initialCampaign?.status === 'ACTIVE' || initialCampaign?.status === 'APPROVED';

  const [approvedGates, setApprovedGates] = useState(() => {
    if (isCampaignApprovedOrActive) {
      return new Set(['brand_voice', 'commercial', 'compliance_deliverability', 'pdpa_privacy']);
    }
    if (initialCampaign?.governance?.gates && Array.isArray(initialCampaign.governance.gates)) {
      return new Set(initialCampaign.governance.gates);
    }
    return new Set(
      initialCampaign?.governance?.allGatesApproved
        ? ['brand_voice', 'commercial', 'compliance_deliverability', 'pdpa_privacy']
        : []
    );
  });
  const [signatureMode, setSignatureMode] = useState(
    initialCampaign?.governance?.signatureType || (isCampaignApprovedOrActive ? 'type' : 'draw')
  );
  const [typedSignature, setTypedSignature] = useState(
    initialCampaign?.approvedBy ||
      initialCampaign?.governance?.signedBy ||
      currentUserName ||
      'Alex Wong (Workspace Owner)'
  );
  const [hasDrawnSignature, setHasDrawnSignature] = useState(
    Boolean(
      isCampaignApprovedOrActive ||
      initialCampaign?.governance?.signatureType === 'draw' ||
      initialCampaign?.governance?.allGatesApproved
    )
  );
  const [isPolicyCertified, setIsPolicyCertified] = useState(
    Boolean(isCampaignApprovedOrActive || initialCampaign?.governance?.isCertified)
  );
  const [isScanning, setIsScanning] = useState(false);
  const signatureCanvasRef = useRef(null);
  const isDrawingRef = useRef(false);

  // ── Step 1 AI Blueprint Generation State ──
  const [isGeneratingBlueprint, setIsGeneratingBlueprint] = useState(false);

  // ── Step 3 AI Offer Matrix Generation State ──
  const [isGeneratingMatrix, setIsGeneratingMatrix] = useState(false);
  const [generatingCardIndex, setGeneratingCardIndex] = useState(null);
  const [activeAngle, setActiveAngle] = useState('roi');
  const [matrixIteration, setMatrixIteration] = useState(1);
  const [hasGeneratedMatrix, setHasGeneratedMatrix] = useState(
    Boolean(
      initialPlaybook ||
      (initialCampaign?.matrix &&
        initialCampaign.matrix.length > 0 &&
        initialCampaign.matrix.some((m) => m.painPoint && m.painPoint.trim()))
    )
  );
  const [hasGeneratedSequence, setHasGeneratedSequence] = useState(
    Boolean(
      initialPlaybook ||
      (initialCampaign?.journey &&
        initialCampaign.journey.length > 0 &&
        initialCampaign.journey.some((j) => j.name && j.name.trim()))
    )
  );

  // ── Step 4 AI Cadence Sequence Generation State ──
  const [isGeneratingSequence, setIsGeneratingSequence] = useState(false);

  // Extract unique roles and attached leads from Step 2's imported leads
  const importedLeadRoles = useMemo(() => {
    if (!formData.importedLeads || formData.importedLeads.length === 0) return [];
    const map = new Map();
    formData.importedLeads.forEach((lead) => {
      const role = (lead.jobTitle || 'Executive Decision Maker').trim();
      if (!map.has(role)) {
        map.set(role, []);
      }
      map.get(role).push(lead);
    });
    return Array.from(map.entries()).map(([role, leads]) => ({
      role,
      leads,
      count: leads.length,
    }));
  }, [formData.importedLeads]);

  // Determine roles to target: prefer imported lead titles/roles from Step 2
  const getRolesToUse = () => {
    if (importedLeadRoles.length > 0) {
      return importedLeadRoles.map((r) => r.role);
    }
    if (formData.targetPersonas && formData.targetPersonas.length > 0) {
      return formData.targetPersonas;
    }
    if (formData.matrix && formData.matrix.length > 0) {
      return formData.matrix.map((m) => m.persona).filter(Boolean);
    }
    return [];
  };

  const [isRoleSyncBannerDismissed, setIsRoleSyncBannerDismissed] = useState(false);

  // Compute summary of mismatched/updated CRM lead roles for Tab 1 reminder banner
  const updatedLeadRolesSummary = useMemo(() => {
    if (!formData.importedLeads || formData.importedLeads.length === 0) return '';
    const matrixRolesSet = new Set(
      (formData.matrix || []).map((m) => (m.persona || '').trim().toLowerCase())
    );
    const mismatched = importedLeadRoles.filter(
      (item) => !matrixRolesSet.has(item.role.toLowerCase())
    );
    if (mismatched.length === 0) return '';
    return mismatched
      .map((item) => {
        const leadNames = item.leads
          .map((l) => {
            const name =
              l.name || `${l.firstName || ''} ${l.lastName || ''}`.trim() || l.email || 'Lead';
            return l.company ? `${name} (${l.company})` : name;
          })
          .filter(Boolean)
          .join(', ');
        return `${leadNames} → Role: ${item.role}`;
      })
      .join('; ');
  }, [formData.importedLeads, formData.matrix, importedLeadRoles]);

  // ── KPI Forecast State & AI Simulation ──
  const [isSimulatingKpis, setIsSimulatingKpis] = useState(false);
  const [kpiAiRationale, setKpiAiRationale] = useState('');

  const handleSimulateKpis = async (overrideScenario) => {
    const sc = overrideScenario || formData.forecastScenario || 'baseline';
    setIsSimulatingKpis(true);
    try {
      const audience =
        formData.importedLeads && formData.importedLeads.length > 0
          ? formData.importedLeads.length
          : formData.finalAudienceCount || 0;
      const reachable = getDynamicReachability(formData.importedLeads, audience);

      const res = await axios.post('/campaign/simulate-kpis', {
        audience,
        reachable,
        scenario: sc,
        avgDealSize: formData.avgDealSize,
        allocatedBudget: formData.allocatedBudget,
        title: formData.title,
        product: formData.product,
        industrySector: formData.industrySector,
        targetRegion: formData.targetRegion,
        targetPersonas: formData.targetPersonas,
        channels: formData.channels,
      });

      if (res.data?.success && res.data?.data) {
        const d = res.data.data;
        if (d.rates) {
          setFormData((prev) => ({
            ...prev,
            forecastScenario: sc,
            kpiRates: {
              ...d.rates,
            },
          }));
        }
        if (d.rationale) {
          setKpiAiRationale(d.rationale);
        }
      }
    } catch (err) {
      console.warn('[CreateCampaign] Error simulating KPIs with AI:', err);
    } finally {
      setIsSimulatingKpis(false);
    }
  };

  const calculatedKPIs = useMemo(() => {
    const audience =
      formData.importedLeads && formData.importedLeads.length > 0
        ? formData.importedLeads.length
        : formData.finalAudienceCount || 0;
    const reachable = getDynamicReachability(formData.importedLeads, audience);

    return calculateCampaignKPIs({
      audience,
      reachable,
      scenario: formData.forecastScenario || 'baseline',
      avgDealSize: formData.avgDealSize,
      allocatedBudget: formData.allocatedBudget,
      customRates: formData.kpiRates || null,
    });
  }, [
    formData.importedLeads,
    formData.finalAudienceCount,
    formData.forecastScenario,
    formData.avgDealSize,
    formData.allocatedBudget,
    formData.kpiRates,
  ]);

  // ── Step Validation & Sequential Locking ──
  const [validationError, setValidationError] = useState('');

  const getStep1MissingFields = () => {
    const missing = [];
    if (!formData.title?.trim()) missing.push('Campaign Title');
    if (!formData.primaryObjective?.trim()) missing.push('Primary Objective');
    if (!formData.product?.trim()) missing.push('Product / Service');
    if (!formData.campaignType) missing.push('Campaign Type');
    if (!formData.industrySector) missing.push('Target Company Type');
    if (!formData.targetRegion) missing.push('Target Region');
    if (!formData.allocatedBudget?.trim()) missing.push('Allocated Budget');
    if (!formData.startDate) missing.push('Start Date');
    if (!formData.endDate) missing.push('End Date');
    if (!formData.durationDays || Number(formData.durationDays) <= 0)
      missing.push('Campaign Duration');
    if (!formData.targetPersonas || formData.targetPersonas.length === 0)
      missing.push('at least 1 Target Persona');
    if (!formData.channels || formData.channels.length === 0)
      missing.push('at least 1 Outreach Channel');
    return missing;
  };

  const validateCurrentStep = (step) => {
    if (step === 1) {
      const missing = getStep1MissingFields();
      if (missing.length > 0) {
        return `Please complete required fields in Step 1: ${missing.join(', ')}.`;
      }
    }
    // Step 2: Leads / audience cohort selection
    if (step === 3) {
      if (!formData.positioning?.trim()) {
        return 'Please provide a Core Positioning Statement in Step 3.';
      }
      if (!formData.matrix || formData.matrix.length === 0) {
        return 'Please generate or add Persona Messaging Frameworks in Step 3.';
      }
      const hasEmptyCard = formData.matrix.some(
        (m) =>
          !m.persona?.trim() ||
          !m.painPoint?.trim() ||
          !(m.valueProp || m.valueProposition)?.trim() ||
          !m.proofPoint?.trim() ||
          !m.cta?.trim() ||
          !m.objection?.trim()
      );
      if (hasEmptyCard) {
        return 'Please complete all required fields for each Persona Messaging Card in Step 3.';
      }
    }
    if (step === 4) {
      if (!formData.journey || formData.journey.length === 0) {
        return 'Please generate or add at least one Outreach Cadence Touchpoint in Step 4.';
      }
      const hasEmptyStep = formData.journey.some(
        (j) => !(j.name?.trim() || j.touchpoint?.trim() || j.action?.trim()) || !j.channel?.trim()
      );
      if (hasEmptyStep) {
        return 'Please complete the message details for all Outreach Cadence Touchpoints in Step 4.';
      }
      const campaignEnd = formData.endDate;
      const campaignDays =
        campaignEnd && formData.startDate
          ? calculateDiffDays(formData.startDate, campaignEnd) + 1
          : Number(formData.durationDays) || 45;
      const stepExceedsEnd = formData.journey.some((j) => {
        if (campaignEnd && j.nominalDate && j.nominalDate > campaignEnd) return true;
        if (j.day && Number(j.day) > campaignDays) return true;
        return false;
      });
      if (stepExceedsEnd) {
        return `All cadence steps must occur within the campaign duration (Day 1 to Day ${campaignDays}, ending ${campaignEnd || 'as configured in Step 1'}).`;
      }
    }
    if (step === 5) {
      if (!formData.avgDealSize || parseCurrencyNumber(formData.avgDealSize) <= 0) {
        return 'Please specify an Average Opportunity Deal Size (RM) in Step 5 for the KPI pipeline simulation.';
      }
    }
    if (step === 6) {
      if (approvedGates.size < 4) {
        return 'All 4 governance compliance gates must be approved in Step 6.';
      }
      if (!isPolicyCertified && !hasDrawnSignature && !(typedSignature && typedSignature.trim())) {
        return 'Policy certification and signatory approval are required in Step 6.';
      }
    }
    return null;
  };

  const isStepCompleted = (step) => {
    if (step === 1) return validateCurrentStep(1) === null;
    if (step === 2) {
      return Boolean(formData.importedLeads && formData.importedLeads.length > 0);
    }
    if (step === 3) return validateCurrentStep(3) === null;
    if (step === 4) return validateCurrentStep(4) === null;
    if (step === 5) return validateCurrentStep(5) === null;
    if (step === 6) return validateCurrentStep(6) === null;
    return false;
  };

  // ── Auto-Sync Matrix & Subsequent Tabs on Navigation ──
  const [isTransitioningStep, setIsTransitioningStep] = useState(false);
  const [transitionStatusText, setTransitionStatusText] = useState('');
  const initialRolesToUse =
    isExistingCampaign || initialPlaybook
      ? formData.importedLeads && formData.importedLeads.length > 0
        ? Array.from(
            new Set(
              formData.importedLeads
                .map((l) => (l.jobTitle || 'Executive Decision Maker').trim())
                .filter(Boolean)
            )
          )
        : formData.targetPersonas && formData.targetPersonas.length > 0
          ? formData.targetPersonas
          : formData.matrix && formData.matrix.length > 0
            ? formData.matrix.map((m) => m.persona).filter(Boolean)
            : ['VP Sales']
      : formData.targetPersonas && formData.targetPersonas.length > 0
        ? formData.targetPersonas
        : null;

  const initialFingerprintStr = calculateLeadFingerprint(initialRolesToUse);
  const initialIsOutOfSync = checkRolesOutOfSync(initialRolesToUse, formData.matrix);

  const lastSyncedLeadFingerprintRef = useRef(initialFingerprintStr);
  const acknowledgedFingerprintsRef = useRef(
    new Set(
      (isExistingCampaign || initialPlaybook) && initialFingerprintStr && !initialIsOutOfSync
        ? [initialFingerprintStr]
        : []
    )
  );

  // Global flag to indicate ANY active AI generation (used for locking interactions, container breathing & catalog exit warning)
  const isAnyAiGenerating = Boolean(
    isGeneratingBlueprint ||
    isTransitioningStep ||
    isGeneratingMatrix ||
    generatingCardIndex !== null ||
    isGeneratingSequence ||
    isSimulatingKpis ||
    isScanning ||
    isSaving
  );

  // Lock navigation across the wizard during any active generation, transition, or save
  const isBlockingNavigation = Boolean(isAnyAiGenerating);

  // Map of active AI generation state per step number (for tab-level thinking animation)
  const stepGeneratingMap = useMemo(
    () => ({
      1: Boolean(isGeneratingBlueprint || (isTransitioningStep && wizardStep === 1)),
      2: false,
      3: Boolean(
        isGeneratingMatrix ||
        generatingCardIndex !== null ||
        (isTransitioningStep && (wizardStep === 2 || wizardStep === 3 || pendingTargetStep === 3))
      ),
      4: Boolean(
        isGeneratingSequence ||
        (isTransitioningStep &&
          (wizardStep === 2 || wizardStep === 3 || wizardStep === 4 || pendingTargetStep === 4))
      ),
      5: Boolean(
        isSimulatingKpis ||
        (isTransitioningStep &&
          (wizardStep === 2 ||
            wizardStep === 3 ||
            wizardStep === 4 ||
            wizardStep === 5 ||
            pendingTargetStep === 5))
      ),
      6: Boolean(isScanning),
    }),
    [
      isGeneratingBlueprint,
      isTransitioningStep,
      wizardStep,
      pendingTargetStep,
      isGeneratingMatrix,
      generatingCardIndex,
      isGeneratingSequence,
      isSimulatingKpis,
      isScanning,
    ]
  );

  const computeCurrentLeadFingerprint = (customRoles = null) => {
    const roles = customRoles || getRolesToUse();
    return calculateLeadFingerprint(roles);
  };

  const isTab2ModifiedSinceLastSync = () => {
    const currentFingerprint = computeCurrentLeadFingerprint();
    return lastSyncedLeadFingerprintRef.current !== currentFingerprint;
  };

  const syncMatrixAndSubsequentContent = async (
    targetStep,
    customRoles = null,
    forceRegenerate = false
  ) => {
    const hasImportedLeads = formData.importedLeads && formData.importedLeads.length > 0;
    const baseRoles = customRoles || getRolesToUse();
    const rolesToUse =
      baseRoles.length > 0
        ? baseRoles
        : ['Vice President of Sales (VP Sales)', 'Chief Revenue Officer (CRO)'];
    const currentFingerprint = computeCurrentLeadFingerprint(rolesToUse);
    const currentMatrixPersonas = (formData.matrix || []).map((m) =>
      (m.persona || '').trim().toLowerCase()
    );
    const expectedRoles = rolesToUse.map((r) => r.trim().toLowerCase());

    if (!forceRegenerate) {
      // In edit/view mode or if matrix already exists, preserve existing content unless explicitly forced
      if (isEditMode && formData.matrix && formData.matrix.length > 0) {
        setWizardStep(targetStep);
        setMaxStepReached((prev) => Math.max(prev, targetStep));
        setValidationError('');
        return;
      }

      // Check if current matrix already matches all expected roles (from Tab 2 leads if present, else Tab 1 personas):
      const isMatchingExpectedRoles =
        formData.matrix &&
        formData.matrix.length === expectedRoles.length &&
        expectedRoles.every((r) => currentMatrixPersonas.includes(r));

      if (isMatchingExpectedRoles) {
        setWizardStep(targetStep);
        setMaxStepReached((prev) => Math.max(prev, targetStep));
        setValidationError('');
        return;
      }
    }

    // 3. ONLY regenerate if user requested AI generation, imported leads with new/unmatched roles, or matrix is empty
    setIsTransitioningStep(true);
    setTransitionStatusText(
      hasImportedLeads
        ? `Generating tailored AI Value Matrix for ${rolesToUse.length} target role${rolesToUse.length > 1 ? 's' : ''} from imported leads...`
        : `Generating AI Value Matrix for target personas...`
    );

    const sampleCompanies = Array.from(
      new Set((formData.importedLeads || []).map((l) => l.company).filter(Boolean))
    );

    try {
      const matrixRes = await axios.post('/campaign/generate-matrix', {
        roles: rolesToUse,
        product: formData.product || 'Enterprise AI Lead Gen & Pipeline Engine',
        productDescription: formData.productDescription,
        primaryObjective: formData.primaryObjective,
        targetRegion: formData.targetRegion || 'Malaysia & Singapore',
        industrySector: formData.industrySector || 'Enterprise SaaS',
        companies: sampleCompanies,
        previousMatrix: formData.matrix,
        angle: activeAngle || 'roi',
        iteration: matrixIteration,
        positioning: formData.positioning,
        painPoints: formData.painPoints,
      });

      let updatedMatrix = null;
      let updatedPositioning = formData.positioning;
      let updatedPainPoints = formData.painPoints;

      if (matrixRes.data && matrixRes.data.data) {
        const { positioning, painPoints, matrix } = matrixRes.data.data;
        if (matrix && matrix.length > 0) {
          updatedMatrix = matrix;
        }
        if (positioning && !formData.positioning?.trim()) {
          updatedPositioning = positioning;
        }
        if (painPoints && !formData.painPoints?.trim()) {
          updatedPainPoints = painPoints;
        }
      }

      if (!updatedMatrix || updatedMatrix.length === 0) {
        const hasCustomPos = Boolean(formData.positioning && formData.positioning.trim());
        const hasCustomPains = Boolean(formData.painPoints && formData.painPoints.trim());

        updatedMatrix = rolesToUse.map((persona) => ({
          persona,
          painPoint: hasCustomPains
            ? `Addressing ${formData.painPoints.trim()} for ${persona} in ${formData.targetRegion || 'target markets'}.`
            : `Managing pipeline unpredictability and manual outbound efforts in ${formData.targetRegion || 'target markets'}.`,
          valueProp: hasCustomPos
            ? `Deliver on ${formData.positioning.trim()} for ${persona}.`
            : `Automated AI lead generation driving high-intent qualified executive meetings for ${formData.product || 'the platform'}.`,
          proofPoint: `Validated 3.5x pipeline acceleration with verified decision-maker direct dials in 45 days.`,
          cta: `Book a 15-minute consultation to review targeted accounts.`,
          objection: `Handling competitor tools: We provide direct data verification and multichannel cadence execution.`,
        }));
      }

      // Concurrently generate Tab 4 Cadence Sequence and Tab 5 KPI Simulation
      let updatedJourney = formData.journey;
      const shouldGenerateJourney =
        !updatedJourney || updatedJourney.length === 0 || forceRegenerate;

      let updatedAvgDealSize = formData.avgDealSize;
      if (!updatedAvgDealSize || parseCurrencyNumber(updatedAvgDealSize) <= 0) {
        updatedAvgDealSize = 35000;
      }

      const audience = hasImportedLeads
        ? formData.importedLeads.length
        : formData.finalAudienceCount || 780;
      const reachable = getDynamicReachability(formData.importedLeads, audience);

      let simulatedRates = formData.kpiRates;
      let simulatedRationale = '';

      const campaignDuration = Number(formData.durationDays) || 45;
      const campaignStart = formData.startDate || formatLocalDate(new Date());
      const campaignEnd = formData.endDate || addDays(campaignStart, campaignDuration);

      const sequencePromise = shouldGenerateJourney
        ? axios
            .post('/campaign/generate-sequence', {
              title: formData.title,
              product: formData.product,
              productDescription: formData.productDescription,
              primaryObjective: formData.primaryObjective,
              industrySector: formData.industrySector,
              targetRegion: formData.targetRegion,
              targetPersonas: rolesToUse,
              channels: formData.channels || ['Email', 'LinkedIn'],
              durationDays: campaignDuration,
              maxTouches:
                formData.schedule?.maxCampaignTouches ||
                formData.strategy?.schedule?.maxCampaignTouches ||
                formData.timingConfig?.maxCampaignTouches ||
                5,
              startDate: campaignStart,
              endDate: campaignEnd,
              positioning: updatedPositioning,
              painPoints: updatedPainPoints,
              matrix: updatedMatrix,
            })
            .catch(() => null)
        : Promise.resolve(null);

      const kpiPromise = axios
        .post('/campaign/simulate-kpis', {
          audience,
          reachable,
          scenario: formData.forecastScenario || 'baseline',
          avgDealSize: updatedAvgDealSize,
          allocatedBudget: formData.allocatedBudget || '$ 25,000',
          title: formData.title,
          product: formData.product,
          industrySector: formData.industrySector,
          targetRegion: formData.targetRegion,
          targetPersonas: rolesToUse,
          channels: formData.channels || ['Email', 'LinkedIn'],
        })
        .catch(() => null);

      const [seqRes, kpiRes] = await Promise.all([sequencePromise, kpiPromise]);

      if (seqRes?.data?.data?.journey && seqRes.data.data.journey.length > 0) {
        const targetTz = formData.schedule?.fixedTimezone || 'Asia/Kuala_Lumpur';
        updatedJourney = alignJourneyToTimingGuardrails({
          journey: seqRes.data.data.journey,
          startDate: campaignStart,
          endDate: campaignEnd,
          config: formData.schedule || DEFAULT_TIMING_CONFIG,
          timezone: targetTz,
          filterToDuration: true,
        });
      }

      if (kpiRes?.data?.success && kpiRes.data?.data) {
        if (kpiRes.data.data.rates) {
          simulatedRates = kpiRes.data.data.rates;
        }
        if (kpiRes.data.data.rationale) {
          simulatedRationale = kpiRes.data.data.rationale;
        }
      }

      if (simulatedRationale) {
        setKpiAiRationale(simulatedRationale);
      }

      lastSyncedLeadFingerprintRef.current = currentFingerprint;
      setHasGeneratedMatrix(true);
      setHasGeneratedSequence(true);

      setFormData((prev) => ({
        ...prev,
        positioning: updatedPositioning,
        painPoints: updatedPainPoints,
        matrix: updatedMatrix,
        journey: updatedJourney && updatedJourney.length > 0 ? updatedJourney : prev.journey,
        avgDealSize: updatedAvgDealSize,
        kpiRates: simulatedRates || prev.kpiRates,
      }));
    } catch (err) {
      console.warn('[CreateCampaign] Matrix sync error:', err);
      const hasCustomPos = Boolean(formData.positioning && formData.positioning.trim());
      const hasCustomPains = Boolean(formData.painPoints && formData.painPoints.trim());

      const fallbackMatrix = rolesToUse.map((persona) => ({
        persona,
        painPoint: hasCustomPains
          ? `Addressing ${formData.painPoints.trim()} for ${persona} in ${formData.targetRegion || 'target markets'}.`
          : `Managing pipeline unpredictability and manual outbound efforts in ${formData.targetRegion || 'target markets'}.`,
        valueProp: hasCustomPos
          ? `Deliver on ${formData.positioning.trim()} for ${persona}.`
          : `Automated AI lead generation driving high-intent qualified executive meetings for ${formData.product || 'the platform'}.`,
        proofPoint: `Validated 3.5x pipeline acceleration with verified decision-maker direct dials in 45 days.`,
        cta: `Book a 15-minute consultation to review targeted accounts.`,
        objection: `Handling competitor tools: We provide direct data verification and multichannel cadence execution.`,
      }));

      lastSyncedLeadFingerprintRef.current = currentFingerprint;
      setHasGeneratedMatrix(true);
      setHasGeneratedSequence(true);

      setFormData((prev) => ({
        ...prev,
        matrix: fallbackMatrix,
      }));
    } finally {
      setIsTransitioningStep(false);
      setWizardStep(targetStep);
      setMaxStepReached((prev) => Math.max(prev, targetStep));
      setValidationError('');
    }
  };

  // ── AI Generation vs Blank Form Selection Prompt Modal (Tab 2 -> Tab 3) ──
  const [isAiGeneratePromptModalOpen, setIsAiGeneratePromptModalOpen] = useState(false);

  const shouldPromptAiRegeneration = (targetStep) => {
    if (targetStep < 3 || wizardStep >= 3) return false;

    // Do not show AI regeneration prompt modal when creating a blueprint or campaign without importing any new leads in Step 2
    const hasImportedLeads = Boolean(formData.importedLeads && formData.importedLeads.length > 0);
    if (!hasImportedLeads) {
      return false;
    }

    const rolesToUse = getRolesToUse();
    const currentFingerprint = computeCurrentLeadFingerprint(rolesToUse);

    // If user has already made a decision (Regenerate or Skip) for this exact persona configuration, don't ask again!
    if (acknowledgedFingerprintsRef.current.has(currentFingerprint)) {
      return false;
    }

    if (!isExistingCampaign && !hasGeneratedMatrix) {
      return true;
    }

    const isOutOfSync = checkRolesOutOfSync(rolesToUse, formData.matrix);
    const isModified = isTab2ModifiedSinceLastSync();

    return isOutOfSync || isModified || !formData.matrix || formData.matrix.length === 0;
  };

  const handleConfirmAiGenerateModal = async () => {
    setIsAiGeneratePromptModalOpen(false);
    if (isExistingCampaign) {
      setIsEditing(true);
    }
    const target = pendingTargetStep || wizardStep || 3;
    const rolesToUse = getRolesToUse();
    const currentFingerprint = computeCurrentLeadFingerprint(rolesToUse);
    acknowledgedFingerprintsRef.current.add(currentFingerprint);
    setHasUnsavedChanges(true);
    await syncMatrixAndSubsequentContent(target, rolesToUse, true);
  };

  const handleConfirmBlankFormModal = () => {
    setIsAiGeneratePromptModalOpen(false);
    if (isExistingCampaign) {
      setIsEditing(true);
    }
    const target = pendingTargetStep || wizardStep || 3;
    const rolesToUse = getRolesToUse();
    const currentFingerprint = computeCurrentLeadFingerprint(rolesToUse);
    acknowledgedFingerprintsRef.current.add(currentFingerprint);
    lastSyncedLeadFingerprintRef.current = currentFingerprint;

    setFormData((prev) => {
      const prevMatrix = prev.matrix || [];

      // If roles changed, provide updated matrix for rolesToUse:
      // Keep existing customized fields for matching roles, and provide clean blank cards for newly selected roles
      const updatedMatrix = rolesToUse.map((persona) => {
        const existingCard = prevMatrix.find(
          (m) => (m.persona || '').trim().toLowerCase() === persona.trim().toLowerCase()
        );
        if (existingCard) {
          return existingCard;
        }
        return {
          persona,
          painPoint: '',
          valueProp: '',
          proofPoint: '',
          cta: '',
          objection: '',
        };
      });

      return {
        ...prev,
        matrix: updatedMatrix,
      };
    });

    setWizardStep(target);
    setMaxStepReached((prev) => Math.max(prev, target));
    setValidationError('');
    setHasUnsavedChanges(true);
  };

  const handleCloseAiGenerateModal = () => {
    setIsAiGeneratePromptModalOpen(false);
    setPendingTargetStep(null);
  };

  // ── AI Generation Interruption & Unsaved Changes Exit Warning Modals ──
  const [isAiInterruptWarningModalOpen, setIsAiInterruptWarningModalOpen] = useState(false);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);

  const handleBackToCatalog = () => {
    if (isAnyAiGenerating) {
      setIsAiInterruptWarningModalOpen(true);
      return;
    }
    const hasModifications = (isExistingCampaign && isEditing) || hasUnsavedChanges;
    if (hasModifications) {
      setIsUnsavedChangesModalOpen(true);
      return;
    }
    onCancel();
  };

  const handleConfirmSaveAndExit = async () => {
    const step3Error = validateCurrentStep(3);
    if (step3Error) {
      setIsUnsavedChangesModalOpen(false);
      setValidationError(step3Error);
      setWizardStep(3);
      return;
    }

    if (canDirectApprove) {
      try {
        await handleFinalize('SAVE_CHANGES');
        setIsUnsavedChangesModalOpen(false);
        setHasUnsavedChanges(false);
        onCancel();
      } catch (err) {
        console.error('Failed to save changes before exit:', err);
      }
    } else {
      setIsUnsavedChangesModalOpen(false);
      setIsMemberChangeRequestModalOpen(true);
    }
  };

  const handleConfirmDiscardAndExit = () => {
    setIsUnsavedChangesModalOpen(false);
    setIsEditing(false);
    setHasUnsavedChanges(false);
    onCancel();
  };

  const handleStepClick = (targetStep) => {
    if (isBlockingNavigation) return;
    if (targetStep === wizardStep) return;

    // In Edit/View mode, allow free jumping between tabs, but prevent skipping incomplete Step 3 forward
    if (isEditMode) {
      if (targetStep >= 3 && wizardStep <= 2 && shouldPromptAiRegeneration(targetStep)) {
        setPendingTargetStep(targetStep);
        setIsAiGeneratePromptModalOpen(true);
        return;
      }

      if (targetStep >= 4 && (isEditing || !isExistingCampaign)) {
        const step3Error = validateCurrentStep(3);
        if (step3Error) {
          setValidationError(step3Error);
          setWizardStep(3);
          return;
        }
      }

      setWizardStep(targetStep);
      setMaxStepReached((prev) => Math.max(prev, targetStep));
      setValidationError('');
      return;
    }

    // Moving backwards to earlier tabs is always allowed smoothly
    if (targetStep < wizardStep) {
      setWizardStep(targetStep);
      setValidationError('');
      return;
    }

    // If advancing from Step 1 or 2 to Step 3 (or later) during manual campaign creation
    if (wizardStep <= 2 && targetStep >= 3 && shouldPromptAiRegeneration(targetStep)) {
      setPendingTargetStep(targetStep);
      setIsAiGeneratePromptModalOpen(true);
      return;
    }

    // If advancing to a forward tab, verify all prerequisite steps (1 through targetStep - 1)
    for (let s = 1; s < targetStep; s++) {
      const stepError = validateCurrentStep(s);
      if (stepError) {
        setValidationError(stepError);
        return;
      }
    }

    // All prerequisite steps are completed/valid, navigate smoothly to targetStep
    setValidationError('');
    setWizardStep(targetStep);
    setMaxStepReached((prev) => Math.max(prev, targetStep));
  };

  const handleNextStep = () => {
    if (isBlockingNavigation) return;
    if (isEditMode) {
      setValidationError('');
      if (wizardStep === 2 && shouldPromptAiRegeneration(3)) {
        setPendingTargetStep(3);
        setIsAiGeneratePromptModalOpen(true);
        return;
      }
      const next = Math.min(6, wizardStep + 1);
      setWizardStep(next);
      return;
    }
    const error = validateCurrentStep(wizardStep);
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError('');
    if (wizardStep === 2) {
      if (shouldPromptAiRegeneration(3)) {
        setPendingTargetStep(3);
        setIsAiGeneratePromptModalOpen(true);
      } else {
        setWizardStep(3);
        setMaxStepReached((m) => Math.max(m, 3));
      }
    } else {
      setWizardStep((prev) => {
        const next = Math.min(6, prev + 1);
        setMaxStepReached((m) => Math.max(m, next));
        return next;
      });
    }
  };

  // ── Lead Pipeline Import Handlers ──
  const fetchPipelineLeads = async () => {
    setIsLoadingPipelineLeads(true);
    try {
      const [pipeRes, activeLeadsRes] = await Promise.allSettled([
        axios.get('/lead-pipeline'),
        axios.get(
          `/campaign/active-leads${initialCampaign?.id ? `?excludeCampaignId=${initialCampaign.id}` : ''}`
        ),
      ]);

      const activeLeads =
        activeLeadsRes.status === 'fulfilled' && Array.isArray(activeLeadsRes.value?.data?.data)
          ? activeLeadsRes.value.data.data
          : [];

      const activeLeadsMap = new Map();
      activeLeads.forEach((al) => {
        if (al.leadId) activeLeadsMap.set(String(al.leadId).trim(), al);
        if (al.leadEmail) activeLeadsMap.set(String(al.leadEmail).trim().toLowerCase(), al);
      });

      const raw = pipeRes.status === 'fulfilled' ? pipeRes.value?.data?.data || [] : [];
      const baseLeads = raw && raw.length > 0 ? raw : SAMPLE_PIPELINE_LEADS;

      const mapped = baseLeads.map((card, idx) => {
        const norm = normalizeLead(card, idx);
        const idKey = String(
          norm.id || norm.leadId || norm.lead_id || norm.shared_item_id || ''
        ).trim();
        const emailKey = String(norm.email || '')
          .trim()
          .toLowerCase();

        const activeAssignment =
          activeLeadsMap.get(idKey) || (emailKey ? activeLeadsMap.get(emailKey) : null);
        if (
          activeAssignment &&
          (!initialCampaign?.id || activeAssignment.campaignId !== initialCampaign.id)
        ) {
          return {
            ...norm,
            inOtherCampaign: true,
            otherCampaignId: activeAssignment.campaignId,
            otherCampaignName: activeAssignment.campaignName,
            otherCampaignStatus: activeAssignment.campaignStatus,
          };
        }
        return norm;
      });

      setPipelineLeads(mapped);
    } catch (e) {
      console.warn('Could not fetch pipeline leads from backend, using sample fallback:', e);
      setPipelineLeads(SAMPLE_PIPELINE_LEADS.map((l, idx) => normalizeLead(l, idx)));
    } finally {
      setIsLoadingPipelineLeads(false);
    }
  };

  const handleOpenImportModal = () => {
    setIsImportModalOpen(true);
    setModalSearch('');
    setModalStageFilter('ALL');
    setModalScoreFilter('ALL');
    setSelectedLeadIds(new Set());
    fetchPipelineLeads();
  };

  const handleToggleSelectLead = (id) => {
    const targetLead = pipelineLeads.find((l) => l.id === id);
    if (targetLead?.inOtherCampaign) return; // Cannot select lead enrolled in another active campaign

    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleToggleSelectAll = (visibleLeads) => {
    const selectableLeads = (visibleLeads || []).filter((l) => !l.inOtherCampaign);
    const allSelected =
      selectableLeads.length > 0 && selectableLeads.every((l) => selectedLeadIds.has(l.id));
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        selectableLeads.forEach((l) => next.delete(l.id));
      } else {
        selectableLeads.forEach((l) => next.add(l.id));
      }
      return next;
    });
  };

  const handleConfirmImport = () => {
    const toImport = pipelineLeads.filter((l) => selectedLeadIds.has(l.id) && !l.inOtherCampaign);
    if (toImport.length === 0) {
      setIsImportModalOpen(false);
      return;
    }

    setFormData((prev) => {
      const existingIds = new Set((prev.importedLeads || []).map((l) => l.id));
      const mergedLeads = [...(prev.importedLeads || [])];
      toImport.forEach((lead) => {
        if (!existingIds.has(lead.id)) {
          const norm = normalizeLead(lead, mergedLeads.length);
          mergedLeads.push(norm);
          existingIds.add(norm.id);
        }
      });

      const highCount = mergedLeads.filter((l) => l.leadScore >= 80).length;
      const medCount = mergedLeads.filter((l) => l.leadScore >= 60 && l.leadScore < 80).length;
      const lowCount = mergedLeads.filter((l) => l.leadScore < 60).length;
      const totalCount = mergedLeads.length;

      // Extract active roles from imported leads
      const activeRoles = Array.from(
        new Set(mergedLeads.map((l) => (l.jobTitle || 'Executive Decision Maker').trim()))
      );

      const updatedMatrix = activeRoles.map((persona) => {
        const existingCard = (prev.matrix || []).find(
          (m) => (m.persona || '').trim().toLowerCase() === persona.trim().toLowerCase()
        );
        return (
          existingCard || {
            persona,
            painPoint: '',
            valueProp: '',
            proofPoint: '',
            cta: '',
            objection: '',
          }
        );
      });

      return {
        ...prev,
        importedLeads: mergedLeads,
        matrix: updatedMatrix,
        finalAudienceCount: Math.max(prev.finalAudienceCount || 0, totalCount),
        priorityHigh: Math.max(prev.priorityHigh || 0, highCount),
        priorityMed: Math.max(prev.priorityMed || 0, medCount),
        priorityLow: Math.max(prev.priorityLow || 0, lowCount),
        audienceUniverse: Math.max(prev.audienceUniverse || 0, totalCount * 4),
        geoMatchCount: Math.max(prev.geoMatchCount || 0, totalCount * 3),
        industryMatchCount: Math.max(prev.industryMatchCount || 0, totalCount * 2),
        personaMatchCount: Math.max(prev.personaMatchCount || 0, totalCount),
      };
    });

    setIsImportModalOpen(false);
    if (validationError) setValidationError('');
  };

  const handleOpenLushaModal = () => {
    setIsLushaModalOpen(true);
  };

  const handleEnrichAndAddLushaLeads = async ({
    selectedContacts,
    enrichType,
    requestId,
    totalCredits,
  }) => {
    if (!selectedContacts || selectedContacts.length === 0) return;

    const contactIds = selectedContacts.map((c) => c.contactId || c.id);
    const revealEmails = enrichType === 'email' || enrichType === 'both';
    const revealPhones = enrichType === 'phone' || enrichType === 'both';

    // 1. Call backend /api/lusha/enrich/contacts
    let enrichedContactsList = [];
    try {
      const enrichRes = await axios.post('/lusha/enrich/contacts', {
        requestId: requestId || `campaign_lusha_${Date.now()}`,
        contactIds,
        userId: 'current-user',
        revealEmails,
        revealPhones,
        expectedCredits: totalCredits,
      });
      if (enrichRes.data?.success || enrichRes.status === 200) {
        enrichedContactsList = enrichRes.data?.contacts || enrichRes.data?.data || [];
      }
    } catch (enrichErr) {
      console.warn(
        'Lusha enrichment API notice, using parsed contact payload for cohort insertion:',
        enrichErr
      );
    }

    // 2. Persist each enriched contact to CRM Lead Pipeline under "New" stage
    const newlyAddedLeads = [];

    for (let idx = 0; idx < selectedContacts.length; idx++) {
      const contact = selectedContacts[idx];
      const matchedEnriched = enrichedContactsList.find(
        (ec) =>
          String(ec.id || ec.contactId || ec.data?.personId) ===
          String(contact.id || contact.contactId)
      );

      const contactData = matchedEnriched?.data || contact.raw?.data || {};
      const revealedEmailList =
        contactData.emailAddresses ||
        contactData.emails ||
        (matchedEnriched?.email ? [matchedEnriched.email] : []) ||
        (contact.email ? [contact.email] : []);
      const revealedPhoneList =
        contactData.phoneNumbers ||
        contactData.phones ||
        (matchedEnriched?.phone ? [matchedEnriched.phone] : []) ||
        (contact.phone ? [contact.phone] : []);

      const leadName =
        matchedEnriched?.name ||
        contactData.fullName ||
        contact.fullName ||
        contact.name ||
        'Enriched Lead';
      const leadTitle =
        matchedEnriched?.jobTitle || contactData.jobTitle || contact.jobTitle || 'Executive';
      const leadCompany =
        matchedEnriched?.companyName ||
        contactData.companyName ||
        contact.company ||
        contact.companyName ||
        'Target Account';
      const leadLocation =
        contactData.location || contact.location || formData.targetRegion || 'Malaysia';
      const leadLinkedin = contactData.linkedinUrl || contact.linkedinUrl || null;

      const rawEmailVal =
        revealedEmailList[0]?.email ||
        revealedEmailList[0]?.value ||
        (typeof revealedEmailList[0] === 'string' ? revealedEmailList[0] : '');
      const leadEmail =
        rawEmailVal ||
        (revealEmails
          ? `${leadName.toLowerCase().replace(/[^a-z0-9]/g, '') || 'lead'}@${(leadCompany || 'company').toLowerCase().replace(/[^a-z0-9]/g, '')}.com`
          : '');

      const rawPhoneVal =
        revealedPhoneList[0]?.number ||
        revealedPhoneList[0]?.phone ||
        (typeof revealedPhoneList[0] === 'string' ? revealedPhoneList[0] : '');
      const leadPhone =
        rawPhoneVal || (revealPhones ? `+60 1${(idx + 2) % 10}-${345 + idx} 6789` : '');

      let createdPipelineRecord = null;
      try {
        const savePipeRes = await axios.post('/lead-pipeline', {
          name: leadName,
          job_title: leadTitle,
          company: leadCompany,
          location: leadLocation,
          linkedin_url: leadLinkedin,
          email: leadEmail,
          phone_number: leadPhone,
          stage: 'New',
          provider: 'lusha',
          provider_id: String(contact.id || contact.contactId),
          source: 'Lusha Campaign Discovery',
          confirm: true,
        });
        if (savePipeRes.data?.success && savePipeRes.data?.data) {
          createdPipelineRecord = savePipeRes.data.data;
        }
      } catch (saveErr) {
        console.warn('Auto-save to CRM lead pipeline fallback handling:', saveErr);
      }

      const normLead = normalizeLead(
        createdPipelineRecord || {
          id: `lusha_${contact.id || idx + 1}_${Date.now()}`,
          leadId: createdPipelineRecord?.leadId || `L-LUSHA-${Date.now()}-${idx}`,
          name: leadName,
          company: leadCompany,
          jobTitle: leadTitle,
          email: leadEmail,
          phone: leadPhone,
          linkedinUrl: leadLinkedin,
          country: leadLocation,
          industry: formData.industrySector || contact.industry || 'Technology & SaaS',
          stage: 'New',
          leadScore: 88,
          provider: 'lusha',
          source: 'Lusha Enriched',
        },
        (formData.importedLeads || []).length + newlyAddedLeads.length
      );

      // Explicitly mark source/provider for UI badges
      normLead.source = 'Lusha Enriched';
      normLead.provider = 'lusha';

      newlyAddedLeads.push(normLead);
    }

    // 3. Merge newly enriched leads into campaign cohort and update matrix/funnel
    setFormData((prev) => {
      const existingIds = new Set((prev.importedLeads || []).map((l) => String(l.id)));
      const mergedLeads = [...(prev.importedLeads || [])];

      newlyAddedLeads.forEach((nl) => {
        if (!existingIds.has(String(nl.id))) {
          mergedLeads.push(nl);
          existingIds.add(String(nl.id));
        }
      });

      const highCount = mergedLeads.filter((l) => l.leadScore >= 80).length;
      const medCount = mergedLeads.filter((l) => l.leadScore >= 60 && l.leadScore < 80).length;
      const lowCount = mergedLeads.filter((l) => l.leadScore < 60).length;
      const totalCount = mergedLeads.length;

      const activeRoles = Array.from(
        new Set(mergedLeads.map((l) => (l.jobTitle || 'Executive Decision Maker').trim()))
      );

      const updatedMatrix = activeRoles.map((persona) => {
        const existingCard = (prev.matrix || []).find(
          (m) => (m.persona || '').trim().toLowerCase() === persona.trim().toLowerCase()
        );
        return (
          existingCard || {
            persona,
            painPoint: '',
            valueProp: '',
            proofPoint: '',
            cta: '',
            objection: '',
          }
        );
      });

      return {
        ...prev,
        importedLeads: mergedLeads,
        matrix: updatedMatrix,
        finalAudienceCount: Math.max(prev.finalAudienceCount || 0, totalCount),
        priorityHigh: Math.max(prev.priorityHigh || 0, highCount),
        priorityMed: Math.max(prev.priorityMed || 0, medCount),
        priorityLow: Math.max(prev.priorityLow || 0, lowCount),
        audienceUniverse: Math.max(prev.audienceUniverse || 0, totalCount * 4),
        geoMatchCount: Math.max(prev.geoMatchCount || 0, totalCount * 3),
        industryMatchCount: Math.max(prev.industryMatchCount || 0, totalCount * 2),
        personaMatchCount: Math.max(prev.personaMatchCount || 0, totalCount),
      };
    });

    if (validationError) setValidationError('');

    // Trigger sync event and refresh pipeline leads
    try {
      window.dispatchEvent(new CustomEvent('lead-changed', { detail: { action: 'created' } }));
    } catch (_) {}
    fetchPipelineLeads();
  };

  const handleRemoveImportedLead = (leadId) => {
    setSelectedImportedIds((prev) => {
      const next = new Set(prev);
      next.delete(leadId);
      return next;
    });
    setFormData((prev) => {
      const filtered = (prev.importedLeads || []).filter((l) => l.id !== leadId);
      const highCount = filtered.filter((l) => l.leadScore >= 80).length;
      const medCount = filtered.filter((l) => l.leadScore >= 60 && l.leadScore < 80).length;
      const lowCount = filtered.filter((l) => l.leadScore < 60).length;

      // When leads are removed, derive the active target roles:
      // If there are still imported leads, use their unique job titles.
      // If ALL imported leads are removed (0 leads), restore Tab 1's targetPersonas!
      const activeRoles =
        filtered.length > 0
          ? Array.from(
              new Set(filtered.map((l) => (l.jobTitle || 'Executive Decision Maker').trim()))
            )
          : prev.targetPersonas && prev.targetPersonas.length > 0
            ? prev.targetPersonas
            : [];

      const updatedMatrix = activeRoles.map((persona) => {
        const existingCard = (prev.matrix || []).find(
          (m) => (m.persona || '').trim().toLowerCase() === persona.trim().toLowerCase()
        );
        return (
          existingCard || {
            persona,
            painPoint: '',
            valueProp: '',
            proofPoint: '',
            cta: '',
            objection: '',
          }
        );
      });

      return {
        ...prev,
        importedLeads: filtered,
        matrix: updatedMatrix,
        finalAudienceCount: filtered.length,
        priorityHigh: highCount,
        priorityMed: medCount,
        priorityLow: lowCount,
      };
    });
  };

  const handleToggleSelectImported = (leadId) => {
    setSelectedImportedIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  };

  const handleToggleSelectAllImported = () => {
    const list = formData.importedLeads || [];
    const isAllSelected = list.length > 0 && list.every((l) => selectedImportedIds.has(l.id));
    setSelectedImportedIds((prev) => {
      const next = new Set(prev);
      if (isAllSelected) {
        list.forEach((l) => next.delete(l.id));
      } else {
        list.forEach((l) => next.add(l.id));
      }
      return next;
    });
  };

  const handleDeleteSelectedImported = () => {
    if (selectedImportedIds.size === 0) return;
    setFormData((prev) => {
      const filtered = (prev.importedLeads || []).filter((l) => !selectedImportedIds.has(l.id));
      const highCount = filtered.filter((l) => l.leadScore >= 80).length;
      const medCount = filtered.filter((l) => l.leadScore >= 60 && l.leadScore < 80).length;
      const lowCount = filtered.filter((l) => l.leadScore < 60).length;

      // When leads are removed, derive the active target roles:
      // If there are still imported leads, use their unique job titles.
      // If ALL imported leads are removed (0 leads), restore Tab 1's targetPersonas!
      const activeRoles =
        filtered.length > 0
          ? Array.from(
              new Set(filtered.map((l) => (l.jobTitle || 'Executive Decision Maker').trim()))
            )
          : prev.targetPersonas && prev.targetPersonas.length > 0
            ? prev.targetPersonas
            : [];

      const updatedMatrix = activeRoles.map((persona) => {
        const existingCard = (prev.matrix || []).find(
          (m) => (m.persona || '').trim().toLowerCase() === persona.trim().toLowerCase()
        );
        return (
          existingCard || {
            persona,
            painPoint: '',
            valueProp: '',
            proofPoint: '',
            cta: '',
            objection: '',
          }
        );
      });

      return {
        ...prev,
        importedLeads: filtered,
        matrix: updatedMatrix,
        finalAudienceCount: filtered.length,
        priorityHigh: highCount,
        priorityMed: medCount,
        priorityLow: lowCount,
      };
    });
    setSelectedImportedIds(new Set());
  };

  const handleApplyPlaybook = (playbook) => {
    const playbookPersonas = (playbook.targetPersonas || []).map((p) => normalizePersonaName(p));
    const rawMatrix = Array.isArray(playbook.matrix) ? playbook.matrix : [];
    const updatedMatrix = playbookPersonas.map((persona) => {
      const match = rawMatrix.find(
        (m) =>
          normalizePersonaName(m.persona).toLowerCase() === persona.toLowerCase() ||
          persona.toLowerCase().includes(normalizePersonaName(m.persona).toLowerCase()) ||
          normalizePersonaName(m.persona).toLowerCase().includes(persona.toLowerCase())
      );

      const painPoint =
        match?.painPoint ||
        match?.pain_point ||
        match?.painPoints ||
        match?.pain ||
        match?.problem ||
        match?.challenge ||
        '';
      const valueProp =
        match?.valueProp || match?.value_prop || match?.valueProposition || match?.solution || '';
      const proofPoint =
        match?.proofPoint ||
        match?.proof_point ||
        match?.proofPoints ||
        match?.proof ||
        match?.metric ||
        '';
      const cta = match?.cta || match?.callToAction || match?.call_to_action || '';
      const objection = match?.objection || match?.objections || match?.objectionHandling || '';

      const fallbackPain =
        playbook.painPoints && playbook.painPoints.trim()
          ? `Addressing ${playbook.painPoints.trim()} for ${persona} in ${playbook.targetRegion || 'target markets'}.`
          : `Managing pipeline unpredictability, manual outbound prospecting, and low decision-maker engagement in ${playbook.targetRegion || 'target markets'}.`;

      const fallbackValue =
        playbook.positioning && playbook.positioning.trim()
          ? `Deliver on ${playbook.positioning.trim()} for ${persona}.`
          : `Automated AI lead generation driving high-intent qualified executive meetings for ${playbook.product || 'the platform'}.`;

      return {
        persona,
        painPoint: painPoint.trim() || fallbackPain,
        valueProp: valueProp.trim() || fallbackValue,
        proofPoint:
          proofPoint.trim() ||
          'Validated 3.5x pipeline acceleration with verified decision-maker direct dials in 45 days.',
        cta: cta.trim() || 'Book a 15-minute consultation to review targeted accounts.',
        objection:
          objection.trim() ||
          'Handling competitor tools: We provide direct data verification and multichannel cadence execution.',
      };
    });

    const defaultJourney =
      playbook.journey && playbook.journey.length > 0
        ? playbook.journey
        : [
            {
              step: 1,
              day: 1,
              channel: (playbook.channels && playbook.channels[0]) || 'Email',
              name: 'Value Hook & Problem Intro',
              condition: 'Initial Touch',
            },
            {
              step: 2,
              day: 4,
              channel: (playbook.channels && playbook.channels[1]) || 'LinkedIn',
              name: 'Connection Request & Social Context',
              condition: 'If No Reply',
            },
            {
              step: 3,
              day: 8,
              channel: (playbook.channels && playbook.channels[0]) || 'Email',
              name: 'Case Study & Verified Proof Points',
              condition: 'If No Reply',
            },
            {
              step: 4,
              day: 14,
              channel: (playbook.channels && playbook.channels[1]) || 'LinkedIn',
              name: 'Executive Consultation Offer',
              condition: 'If No Reply',
            },
            {
              step: 5,
              day: 21,
              channel: (playbook.channels && playbook.channels[0]) || 'Email',
              name: 'Final Break-up & Industry Benchmark Report',
              condition: 'If No Reply',
            },
          ];

    const finalAud = playbook.finalAudienceCount || 780;
    const audUniv = playbook.audienceUniverse || finalAud * 10;
    const startStr = playbook.startDate || formatLocalDate(new Date());
    const duration = parseInt(playbook.durationDays, 10) || 45;
    const endStr = playbook.endDate || addDays(startStr, duration);

    setFormData({
      title: playbook.name,
      primaryObjective: playbook.primaryObjective,
      campaignType: playbook.campaignType || CAMPAIGN_TYPES[0],
      industrySector: playbook.industrySector || INDUSTRY_SECTORS[0],
      targetRegion: playbook.targetRegion || TARGET_REGIONS[0],
      currency: playbook.currency || 'USD',
      allocatedBudget: playbook.allocatedBudget || '$ 25,000',
      startDate: startStr,
      endDate: endStr,
      durationDays: duration,
      product: playbook.product || '',
      productDescription: playbook.productDescription || '',
      targetPersonas: playbookPersonas,
      channels: playbook.channels || ['Email', 'LinkedIn'],
      avgDealSize: playbook.avgDealSize || 35000,
      audienceUniverse: audUniv,
      geoMatchCount: playbook.geoMatchCount || Math.round(audUniv * 0.67),
      industryMatchCount: playbook.industryMatchCount || Math.round(audUniv * 0.35),
      personaMatchCount: playbook.personaMatchCount || Math.round(audUniv * 0.11),
      exclusionsCount: playbook.exclusionsCount || 140,
      finalAudienceCount: finalAud,
      priorityHigh: playbook.priorityHigh || Math.round(finalAud * 0.38),
      priorityMed: playbook.priorityMed || Math.round(finalAud * 0.45),
      priorityLow: playbook.priorityLow || Math.round(finalAud * 0.17),
      importedLeads: [],
      positioning:
        playbook.positioning ||
        `Empower ${(playbook.targetPersonas || []).join(' / ')} with ${playbook.product || 'AI Outbound'} in ${playbook.targetRegion || 'target markets'}.`,
      painPoints:
        playbook.painPoints ||
        'Low outbound reply rates, manual SDR prospect research, and CRM data decay.',
      matrix: updatedMatrix,
      journey: defaultJourney,
      forecastScenario: 'baseline',
      reviewerRole: 'Sales Manager',
      submissionNotes: playbook.submissionNotes || '',
    });

    lastSyncedLeadFingerprintRef.current = `PERSONAS:${playbookPersonas
      .map((p) => p.trim().toLowerCase())
      .sort()
      .join('|')}`;
    setHasGeneratedMatrix(true);
    setHasGeneratedSequence(true);
    setWizardStep(1);
    setMaxStepReached(isEditMode ? 6 : 1);
    setValidationError('');
    setApprovedGates(new Set());
    setIsPolicyCertified(false);
    setHasDrawnSignature(false);
  };

  const handleTogglePersona = (persona) => {
    if (validationError) setValidationError('');
    setFormData((prev) => {
      const exists = prev.targetPersonas.includes(persona);
      if (exists && prev.targetPersonas.length === 1) return prev;
      const updatedPersonas = exists
        ? prev.targetPersonas.filter((p) => p !== persona)
        : [...prev.targetPersonas, persona];

      const hasLeads = prev.importedLeads && prev.importedLeads.length > 0;
      let updatedMatrix = prev.matrix;
      if (!hasLeads) {
        lastSyncedLeadFingerprintRef.current = `PERSONAS:${updatedPersonas
          .map((p) => p.trim().toLowerCase())
          .sort()
          .join('|')}`;
        updatedMatrix = updatedPersonas.map((p) => {
          const existing = (prev.matrix || []).find(
            (m) => (m.persona || '').trim().toLowerCase() === p.trim().toLowerCase()
          );
          if (existing) return existing;
          return {
            persona: p,
            painPoint: '',
            valueProp: '',
            proofPoint: '',
            cta: '',
            objection: '',
          };
        });
      }

      return {
        ...prev,
        targetPersonas: updatedPersonas,
        matrix: updatedMatrix,
      };
    });
  };

  const handleToggleChannel = (channelId) => {
    if (validationError) setValidationError('');
    setFormData((prev) => {
      const exists = prev.channels.includes(channelId);
      if (exists && prev.channels.length === 1) return prev;
      const updated = exists
        ? prev.channels.filter((c) => c !== channelId)
        : [...prev.channels, channelId];
      return { ...prev, channels: updated };
    });
  };

  const handleRegenerateAudience = () => {
    if (validationError) setValidationError('');
    const randomUniverse = 7000 + Math.floor(Math.random() * 3000);
    const randomGeo = Math.floor(randomUniverse * 0.7);
    const randomInd = Math.floor(randomGeo * 0.55);
    const randomPer = Math.floor(randomInd * 0.45);
    const exclusions = Math.floor(randomPer * 0.15);
    const finalAud = randomPer - exclusions;

    setFormData((prev) => ({
      ...prev,
      audienceUniverse: randomUniverse,
      geoMatchCount: randomGeo,
      industryMatchCount: randomInd,
      personaMatchCount: randomPer,
      exclusionsCount: exclusions,
      finalAudienceCount: finalAud,
      priorityHigh: Math.floor(finalAud * 0.35),
      priorityMed: Math.floor(finalAud * 0.45),
      priorityLow: Math.floor(finalAud * 0.2),
    }));
  };

  const handleRegenerateMatrix = async (customAngle = null) => {
    setIsGeneratingMatrix(true);
    const nextIteration = matrixIteration + 1;
    setMatrixIteration(nextIteration);
    const angleToUse = customAngle || activeAngle;
    if (customAngle) setActiveAngle(customAngle);

    const rolesToUse = getRolesToUse();
    const sampleCompanies = Array.from(
      new Set((formData.importedLeads || []).map((l) => l.company).filter(Boolean))
    );

    try {
      const response = await axios.post('/campaign/generate-matrix', {
        roles: rolesToUse,
        product: formData.product || 'Enterprise AI Lead Gen & Pipeline Engine',
        productDescription: formData.productDescription,
        primaryObjective: formData.primaryObjective,
        targetRegion: formData.targetRegion || 'Malaysia & Singapore',
        industrySector: formData.industrySector || 'Enterprise SaaS',
        companies: sampleCompanies,
        previousMatrix: formData.matrix,
        angle: angleToUse,
        iteration: nextIteration,
        positioning: formData.positioning,
        painPoints: formData.painPoints,
      });

      if (response.data && response.data.data) {
        const { positioning, painPoints, matrix } = response.data.data;
        setHasGeneratedMatrix(true);
        setFormData((prev) => ({
          ...prev,
          positioning: prev.positioning?.trim()
            ? prev.positioning
            : positioning || prev.positioning,
          painPoints: prev.painPoints?.trim() ? prev.painPoints : painPoints || prev.painPoints,
          matrix: matrix || prev.matrix,
        }));
      }
    } catch (err) {
      console.warn(
        '[CreateCampaign] LLM generate-matrix request failed, using local fallback:',
        err
      );
      const hasCustomPos = Boolean(formData.positioning && formData.positioning.trim());
      const hasCustomPains = Boolean(formData.painPoints && formData.painPoints.trim());

      // Local fallback with angle adjustments and user positioning/painPoints
      const updatedMatrix = rolesToUse.map((persona) => {
        let proof =
          'Validated 3.5x pipeline acceleration with verified decision-maker direct dials in 45 days.';
        if (angleToUse === 'roi')
          proof = 'Demonstrated 3.8x ROI and 28% reduction in CAC across enterprise cohorts.';
        if (angleToUse === 'velocity')
          proof = 'Compressed discovery booking cycles from 3 weeks down to 4.5 days.';
        if (angleToUse === 'governance')
          proof = '100% PDPA compliance with 98.4% deliverability across corporate domains.';
        if (angleToUse === 'competitive')
          proof = 'Secured first-mover category advantage over legacy outreach vendors.';

        return {
          persona,
          painPoint: hasCustomPains
            ? `Addressing ${formData.painPoints.trim()} for ${persona} in ${formData.targetRegion || 'target markets'}.`
            : `Managing pipeline unpredictability and manual outbound efforts in ${formData.targetRegion || 'target markets'}.`,
          valueProp: hasCustomPos
            ? `Deliver on ${formData.positioning.trim()} for ${persona}.`
            : `Automated AI lead generation driving high-intent qualified meetings for ${formData.product || 'the platform'}.`,
          proofPoint: proof,
          cta: `Book a 15-minute consultation to review targeted accounts.`,
          objection: `Handling competitor tools: We provide direct data verification and multichannel cadence execution.`,
        };
      });

      setHasGeneratedMatrix(true);
      setFormData((prev) => ({
        ...prev,
        positioning:
          prev.positioning ||
          `Empower ${rolesToUse.join(' / ')} to capture qualified sales meetings in ${formData.targetRegion || 'target markets'}.`,
        painPoints:
          prev.painPoints ||
          'Low outbound reply rates, manual SDR prospect research, and CRM data decay.',
        matrix: updatedMatrix,
      }));
    } finally {
      setIsGeneratingMatrix(false);
    }
  };

  const handleRegenerateSingleCard = async (cardIndex, customAngle = null) => {
    const currentCard = formData.matrix[cardIndex];
    if (!currentCard) return;

    setGeneratingCardIndex(cardIndex);
    const angleToUse = customAngle || activeAngle;
    const sampleCompanies = Array.from(
      new Set((formData.importedLeads || []).map((l) => l.company).filter(Boolean))
    );

    try {
      const response = await axios.post('/campaign/generate-matrix', {
        roles: [currentCard.persona],
        product: formData.product || 'Enterprise AI Lead Gen & Pipeline Engine',
        productDescription: formData.productDescription,
        primaryObjective: formData.primaryObjective,
        targetRegion: formData.targetRegion || 'Malaysia & Singapore',
        industrySector: formData.industrySector || 'Enterprise SaaS',
        companies: sampleCompanies,
        previousMatrix: [currentCard],
        angle: angleToUse,
        iteration: matrixIteration + 1,
        positioning: formData.positioning,
        painPoints: formData.painPoints,
      });

      if (response.data?.data?.matrix?.[0]) {
        const newCard = response.data.data.matrix[0];
        setHasGeneratedMatrix(true);
        setFormData((prev) => {
          const updated = [...prev.matrix];
          updated[cardIndex] = newCard;
          return { ...prev, matrix: updated };
        });
      }
    } catch (err) {
      console.warn('[CreateCampaign] Single card regeneration error:', err);
    } finally {
      setGeneratingCardIndex(null);
    }
  };

  const handleUpdateMatrixRow = (index, field, value) => {
    if (isGeneratingMatrix || generatingCardIndex !== null) return;
    setFormData((prev) => {
      const updated = [...prev.matrix];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, matrix: updated };
    });
  };

  const handleRemoveMatrixRow = (index) => {
    if (isGeneratingMatrix || generatingCardIndex !== null) return;
    setFormData((prev) => {
      const updated = prev.matrix.filter((_, idx) => idx !== index);
      return { ...prev, matrix: updated };
    });
  };

  const handleAddMatrixRole = (roleName) => {
    if (isGeneratingMatrix || generatingCardIndex !== null) return;
    const validPool = getRolesToUse();
    const isAllowed = validPool.some((r) => r.toLowerCase() === roleName.toLowerCase());
    if (!isAllowed) return;

    setFormData((prev) => {
      if (prev.matrix.some((m) => m.persona.toLowerCase() === roleName.toLowerCase())) {
        return prev;
      }

      const newCard = {
        persona: roleName,
        painPoint: '',
        valueProp: '',
        proofPoint: '',
        cta: '',
        objection: '',
      };

      return {
        ...prev,
        matrix: [...prev.matrix, newCard],
      };
    });
  };

  // ── Step 4 Handlers ──
  const handleUpdateJourneyStep = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.journey];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, journey: updated };
    });
  };

  const handleAddJourneyStep = () => {
    setFormData((prev) => {
      const nextStepNum = prev.journey.length + 1;
      const lastDay = prev.journey.length > 0 ? prev.journey[prev.journey.length - 1].day : 1;
      const fallbackChannel =
        prev.channels && prev.channels.length > 0 ? prev.channels[0] : 'Email';
      return {
        ...prev,
        journey: [
          ...prev.journey,
          {
            step: nextStepNum,
            day: nextStepNum === 1 ? 1 : Math.min(Number(prev.durationDays) || 45, lastDay + 4),
            channel: fallbackChannel,
            name: '',
            condition: nextStepNum === 1 ? 'Initial Touch' : 'If No Reply',
          },
        ],
      };
    });
  };

  const handleRemoveJourneyStep = (index) => {
    setFormData((prev) => {
      const filtered = prev.journey.filter((_, idx) => idx !== index);
      const reindexed = filtered.map((item, idx) => ({ ...item, step: idx + 1 }));
      return { ...prev, journey: reindexed };
    });
  };

  // ── Step 6 Handlers ──
  const handleToggleGate = (gateId) => {
    setApprovedGates((prev) => {
      const next = new Set(prev);
      if (next.has(gateId)) {
        next.delete(gateId);
      } else {
        next.add(gateId);
      }
      return next;
    });
  };

  const handleStartDraw = (e) => {
    if (approvedGates.size < 4) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (clientX === undefined || clientY === undefined) return;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    ctx.beginPath();
    ctx.moveTo(x, y);
    isDrawingRef.current = true;
  };

  const handleDraw = (e) => {
    if (!isDrawingRef.current || approvedGates.size < 4) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (clientX === undefined || clientY === undefined) return;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawnSignature(true);
  };

  const handleStopDraw = () => {
    isDrawingRef.current = false;
  };

  const handleClearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setHasDrawnSignature(false);
    setTypedSignature('');
  };

  const handleTriggerBiometricScan = () => {
    if (approvedGates.size < 4) return;
    setIsScanning(true);
    setTimeout(() => {
      setIsScanning(false);
      setIsPolicyCertified(true);
    }, 700);
  };

  const handleRequestChanges = () => {
    setIsRequestChangesModalOpen(true);
  };

  const handleConfirmRevisionRequest = (feedbackNotes) => {
    setFormData((prev) => ({
      ...prev,
      submissionNotes: feedbackNotes,
      revisionFeedback: feedbackNotes,
    }));

    handleFinalize('REQUEST_CHANGES', {
      revisionFeedback: feedbackNotes,
    });
  };

  const handleConfirmMemberChangeRequest = (changeNotes, payload = {}) => {
    const fullNotes = typeof changeNotes === 'string' ? changeNotes : payload.fullNotes || '';
    const selectedSections = payload.selectedSections || [];
    const selectedTags = payload.selectedTags || [];

    setFormData((prev) => ({
      ...prev,
      submissionNotes: fullNotes,
      modifiedSections: selectedSections,
      changeReasonTags: selectedTags,
    }));

    handleFinalize('SUBMIT_APPROVAL', {
      submissionNotes: fullNotes,
      modifiedSections: selectedSections,
      changeReasonTags: selectedTags,
      changeRequestedBy: `${currentUserName} (${userRoleLabel || 'Sales Member'})`,
      changeRequestedAt: new Date().toISOString(),
      isMemberChangeRequest: true,
    });
  };

  const handleConfirmExecutiveApproval = (signPayload = {}) => {
    if (signPayload.approvedGates) {
      setApprovedGates(new Set(signPayload.approvedGates));
    }
    if (signPayload.signatureType) {
      setSignatureMode(signPayload.signatureType);
    }
    if (signPayload.typedSignature) {
      setTypedSignature(signPayload.typedSignature);
    }
    setIsPolicyCertified(true);
    setHasDrawnSignature(true);

    handleFinalize('DIRECT_ACTIVATE', {
      ...signPayload,
      isReapproval: true,
      allGatesApproved: true,
      isCertified: true,
    });
  };

  const handleFinalize = async (actionType, extraData = {}) => {
    // 1. Validate Step 1 Brief & Goals for saving/activating/submitting
    if (
      [
        'DIRECT_ACTIVATE',
        'SUBMIT_APPROVAL',
        'DIRECT_APPROVE',
        'SAVE_CHANGES',
        'SAVE_DRAFT',
      ].includes(actionType)
    ) {
      const step1Error = validateCurrentStep(1);
      if (step1Error) {
        setValidationError(step1Error);
        setWizardStep(1);
        return;
      }

      // 2. Validate Step 3 Persona Matrix completeness (cannot be saved when empty)
      const step3Error = validateCurrentStep(3);
      if (step3Error) {
        setValidationError(step3Error);
        setWizardStep(3);
        return;
      }
    }

    // 3. Enforce target leads presence before activation or manager submission
    if (actionType === 'DIRECT_ACTIVATE' || actionType === 'SUBMIT_APPROVAL') {
      const leads = formData.importedLeads || [];
      if (leads.length === 0) {
        setValidationError(
          'Cannot create & activate campaign without target leads. Please import leads in Step 2 (Target Cohort).'
        );
        setWizardStep(2);
        return;
      }
    }

    let status = initialCampaign?.status || 'DRAFT';
    let approvedBy = initialCampaign?.approvedBy || null;
    let approvedAt = initialCampaign?.approvedAt || null;

    let pausedBy = initialCampaign?.pausedBy || null;
    let pausedAt = initialCampaign?.pausedAt || null;
    let pauseRequestedBy = initialCampaign?.pauseRequestedBy || null;
    let pauseRequestedAt = initialCampaign?.pauseRequestedAt || null;

    if (actionType === 'SUBMIT_APPROVAL') {
      status = 'PENDING_APPROVAL';
    } else if (actionType === 'REQUEST_CHANGES') {
      status = 'CHANGES_REQUESTED';
    } else if (actionType === 'DIRECT_APPROVE') {
      status = 'APPROVED';
      approvedBy = `${currentUserName} (${userRoleLabel})`;
      approvedAt = new Date().toISOString();
    } else if (actionType === 'DIRECT_ACTIVATE') {
      status = 'ACTIVE';
      approvedBy = approvedBy || `${currentUserName} (${userRoleLabel})`;
      approvedAt = new Date().toISOString();
      pauseRequestedBy = null;
      pauseRequestedAt = null;
    } else if (actionType === 'PAUSE') {
      const hasPauseRequest = Boolean(
        initialCampaign?.pauseRequestedBy || initialCampaign?.governance?.pauseRequestedBy
      );
      if (hasPauseRequest && canDirectApprove) {
        if (!pauseCheck.canApprove) {
          setValidationError(
            pauseCheck.reason || 'Insufficient permissions to approve this pause request.'
          );
          return;
        }
      }
      status = 'PAUSED';
      pausedBy = `${currentUserName} (${userRoleLabel})`;
      pausedAt = new Date().toISOString();
      pauseRequestedBy = null;
      pauseRequestedAt = null;
    } else if (actionType === 'RESUME') {
      status = 'ACTIVE';
      pausedBy = null;
      pausedAt = null;
      pauseRequestedBy = null;
      pauseRequestedAt = null;
    } else if (actionType === 'REQUEST_PAUSE') {
      status = 'ACTIVE';
      pauseRequestedBy = `${currentUserName} (${userRoleLabel})`;
      pauseRequestedAt = new Date().toISOString();
    } else if (actionType === 'REJECT_PAUSE_REQUEST') {
      status = 'ACTIVE';
      pauseRequestedBy = null;
      pauseRequestedAt = null;
    } else if (actionType === 'SAVE_CHANGES') {
      status = initialCampaign?.status || 'DRAFT';
    } else if (actionType === 'SAVE_DRAFT') {
      status = 'DRAFT';
    }

    let signatureData =
      extraData.signatureData || initialCampaign?.governance?.signatureData || null;
    if (
      signatureMode === 'draw' &&
      signatureCanvasRef.current &&
      hasDrawnSignature &&
      !extraData.signatureData
    ) {
      try {
        signatureData = signatureCanvasRef.current.toDataURL('image/png');
      } catch (_err) {
        // Fallback to existing signatureData
      }
    } else if (signatureMode === 'type' && !extraData.signatureData) {
      signatureData = null;
    }

    const isNowApprovedOrActive =
      status === 'APPROVED' ||
      status === 'ACTIVE' ||
      (status === 'PAUSED' && initialCampaign?.governance?.isCertified);

    let finalSignatureType = isNowApprovedOrActive
      ? extraData.signatureType || signatureMode
      : null;
    let finalSignatureData = isNowApprovedOrActive
      ? extraData.signatureData !== undefined
        ? extraData.signatureData
        : signatureData
      : null;
    let finalTypedSignature = isNowApprovedOrActive
      ? extraData.typedSignature !== undefined
        ? extraData.typedSignature
        : typedSignature || null
      : null;
    let finalSignedBy = isNowApprovedOrActive
      ? extraData.signedBy ||
        (canDirectApprove
          ? `${currentUserName} (${userRoleLabel || 'Sales Manager'})`
          : initialCampaign?.governance?.signedBy ||
            initialCampaign?.approvedBy ||
            'Alex Wong (Workspace Owner)')
      : null;

    if (actionType === 'DIRECT_APPROVE' || actionType === 'DIRECT_ACTIVATE') {
      finalSignedBy =
        extraData.signedBy || `${currentUserName} (${userRoleLabel || 'Sales Manager'})`;
    }

    const isReapproving =
      (actionType === 'DIRECT_APPROVE' || actionType === 'DIRECT_ACTIVATE') &&
      (Boolean(extraData.isReapproval) ||
        initialCampaign?.status === 'PENDING_APPROVAL' ||
        initialCampaign?.status === 'CHANGES_REQUESTED' ||
        Boolean(initialCampaign?.submissionNotes));

    const certId = isReapproving
      ? `GOV-CERT-2026-${Math.floor(100000 + Math.random() * 900000)}`
      : initialCampaign?.governance?.certId ||
        `GOV-CERT-2026-${Math.floor(100000 + Math.random() * 900000)}`;

    const certHash = isReapproving
      ? `sha256:${Array.from({ length: 64 }, () => Math.floor(Math.random() * 16).toString(16)).join('')}`
      : initialCampaign?.governance?.certHash ||
        `sha256:${Array.from({ length: 64 }, () => Math.floor(Math.random() * 16).toString(16)).join('')}`;

    // Helper to compute initials for audit avatars
    const computeAuditInitials = (nameStr = '') => {
      const clean = String(nameStr)
        .replace(/\(.*?\)/g, '')
        .trim();
      const parts = clean.split(/\s+/).filter(Boolean);
      if (parts.length === 0) return 'EX';
      if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    // Build or append to the immutable governance audit history array
    const auditHistory = Array.isArray(initialCampaign?.governance?.history)
      ? [...initialCampaign.governance.history]
      : [];

    const currentActorStr = currentUserName
      ? `${currentUserName} (${userRoleLabel || 'Sales Member'})`
      : 'Sales Strategist';
    const nowIso = new Date().toISOString();

    // 1. Seed initial proposal if history is empty
    if (auditHistory.length === 0) {
      const origProposer = initialCampaign?.createdBy || currentActorStr;
      const origCreatedTime = initialCampaign?.createdAt || nowIso;
      auditHistory.push({
        id: `gov-evt-prop-${initialCampaign?.id || Date.now()}`,
        type: 'PROPOSAL_SUBMITTED',
        title: 'Strategy Proposal Submitted',
        name: origProposer,
        role: (String(origProposer).match(/\((.*?)\)/) || [])[1] || 'Sales Member',
        datetime: origCreatedTime,
        timestamp: origCreatedTime,
        avatarText: computeAuditInitials(origProposer),
        avatarColor: 'blue',
        badge: 'Proposed',
        badgeColor: 'badge-blue',
        note:
          initialCampaign?.submissionNotes ||
          formData?.submissionNotes ||
          'Strategy, target criteria, budget, and messaging parameters submitted for executive review.',
      });

      const origApprovedBy =
        initialCampaign?.governance?.originalApprovedBy ||
        initialCampaign?.approvedBy ||
        initialCampaign?.governance?.signedBy;
      const origApprovedAt =
        initialCampaign?.governance?.originalApprovedAt ||
        initialCampaign?.approvedAt ||
        initialCampaign?.governance?.approvedAt;

      if (
        origApprovedBy &&
        (initialCampaign?.status === 'APPROVED' ||
          initialCampaign?.status === 'ACTIVE' ||
          initialCampaign?.status === 'PAUSED')
      ) {
        const initialSeedCert = initialCampaign?.governance?.certId || certId;
        auditHistory.push({
          id: `gov-evt-appr-${initialCampaign?.id || Date.now()}`,
          type: 'CAMPAIGN_APPROVED',
          title: 'Governance Certification & Campaign Approval',
          name: origApprovedBy,
          role: (String(origApprovedBy).match(/\((.*?)\)/) || [])[1] || 'Sales Manager',
          datetime: origApprovedAt || nowIso,
          timestamp: origApprovedAt || nowIso,
          avatarText: computeAuditInitials(origApprovedBy),
          avatarColor: 'emerald',
          badge: 'Certified',
          badgeColor: 'badge-green',
          note: `All 4 governance gates certified with digital signature (${initialSeedCert}).`,
          certId: initialSeedCert,
          certHash: initialCampaign?.governance?.certHash,
        });
      }

      const priorPauseReq =
        initialCampaign?.pauseRequestedBy || initialCampaign?.governance?.pauseRequestedBy;
      const priorPauseReqAt =
        initialCampaign?.pauseRequestedAt || initialCampaign?.governance?.pauseRequestedAt;
      if (priorPauseReq) {
        auditHistory.push({
          id: `gov-evt-preq-${initialCampaign?.id || Date.now()}`,
          type: 'PAUSE_REQUESTED',
          title: 'Campaign Pause Request Submitted',
          name: priorPauseReq,
          role: (String(priorPauseReq).match(/\((.*?)\)/) || [])[1] || 'Sales Member',
          datetime: priorPauseReqAt || nowIso,
          timestamp: priorPauseReqAt || nowIso,
          avatarText: computeAuditInitials(priorPauseReq),
          avatarColor: 'amber',
          badge: 'Pause Requested',
          badgeColor: 'badge-amber',
          note: 'Temporary suspension requested for audience targeting & copy adjustments.',
        });
      }
    }

    // 2. Append new lifecycle action events without replacing past records
    if (actionType === 'REQUEST_PAUSE') {
      auditHistory.push({
        id: `gov-evt-${Date.now()}-pause-req`,
        type: 'PAUSE_REQUESTED',
        title: 'Campaign Pause Request Submitted',
        name: currentActorStr,
        role: userRoleLabel,
        datetime: nowIso,
        timestamp: nowIso,
        avatarText: computeAuditInitials(currentUserName),
        avatarColor: 'amber',
        badge: 'Pause Requested',
        badgeColor: 'badge-amber',
        note: 'Temporary suspension requested for audience targeting & copy adjustments.',
      });
    } else if (actionType === 'DIRECT_APPROVE' || actionType === 'DIRECT_ACTIVATE') {
      const alreadyApproved = auditHistory.some(
        (h) => h.type === 'CAMPAIGN_APPROVED' && h.name === currentActorStr
      );
      if (!alreadyApproved) {
        auditHistory.push({
          id: `gov-evt-${Date.now()}-approved`,
          type: 'CAMPAIGN_APPROVED',
          title: 'Governance Certification & Campaign Approval',
          name: currentActorStr,
          role: userRoleLabel,
          datetime: nowIso,
          timestamp: nowIso,
          avatarText: computeAuditInitials(currentUserName),
          avatarColor: 'emerald',
          badge: 'Certified',
          badgeColor: 'badge-green',
          note: `All 4 governance gates certified with digital signature (${certId}).`,
          certId,
          certHash,
        });
      }
      if (status === 'ACTIVE' || actionType === 'DIRECT_ACTIVATE') {
        auditHistory.push({
          id: `gov-evt-${Date.now()}-active`,
          type: 'CAMPAIGN_ACTIVATED',
          title: 'Autonomous Multi-Touch Outreach Active',
          name: currentActorStr,
          role: 'Campaign Execution',
          datetime: nowIso,
          timestamp: nowIso,
          avatarText: 'AI',
          avatarColor: 'cyan',
          badge: 'Live Active',
          badgeColor: 'badge-cyan',
          note: 'Outbound Engine actively dispatching multi-touch cadence sequences.',
        });
      }
    } else if (actionType === 'PAUSE') {
      auditHistory.push({
        id: `gov-evt-${Date.now()}-paused`,
        type: 'CAMPAIGN_PAUSED',
        title: 'Pause Approved & Outreach Suspended',
        name: currentActorStr,
        role: userRoleLabel,
        datetime: nowIso,
        timestamp: nowIso,
        avatarText: computeAuditInitials(currentUserName),
        avatarColor: 'orange',
        badge: 'Suspended',
        badgeColor: 'badge-orange',
        note: 'Executive sign-off completed; live outbound messaging paused.',
      });
    } else if (actionType === 'RESUME') {
      auditHistory.push({
        id: `gov-evt-${Date.now()}-resumed`,
        type: 'CAMPAIGN_RESUMED',
        title: 'Campaign Resumed & Outreach Active',
        name: currentActorStr,
        role: userRoleLabel,
        datetime: nowIso,
        timestamp: nowIso,
        avatarText: 'AI',
        avatarColor: 'cyan',
        badge: 'Live Active',
        badgeColor: 'badge-cyan',
        note: 'Outreach execution resumed across verified channels.',
      });
    } else if (actionType === 'SUBMIT_APPROVAL') {
      const isRevision = initialCampaign?.status === 'CHANGES_REQUESTED';
      const changeSections = extraData.modifiedSections ||
        formData.modifiedSections ||
        initialCampaign?.modifiedSections || [
          'Step 1: Brief & Goals',
          'Step 2: Target Cohorts & Leads',
        ];
      const changeSectionsStr = Array.isArray(changeSections)
        ? changeSections.join(', ')
        : String(changeSections);
      const changeTags =
        extraData.changeReasonTags ||
        formData.changeReasonTags ||
        initialCampaign?.changeReasonTags ||
        [];
      const changeTagsStr =
        Array.isArray(changeTags) && changeTags.length > 0 ? ` [${changeTags.join(', ')}]` : '';
      const finalNote =
        extraData.submissionNotes ||
        formData.submissionNotes ||
        (isRevision
          ? 'Resubmitted updated campaign blueprint addressing manager review feedback.'
          : 'Campaign strategy blueprint submitted for executive governance sign-off.');

      auditHistory.push({
        id: `gov-evt-${Date.now()}-change-req`,
        type: isRevision ? 'REVISION_SUBMITTED' : 'CHANGE_REQUEST_SUBMITTED',
        title: isRevision
          ? 'Revised Campaign Strategy Resubmitted'
          : 'Campaign Change Request Submitted',
        name: currentActorStr,
        role: userRoleLabel || 'Sales Member',
        datetime: nowIso,
        timestamp: nowIso,
        avatarText: computeAuditInitials(currentUserName),
        avatarColor: 'blue',
        badge: isRevision ? 'Resubmitted' : 'Changes Proposed',
        badgeColor: 'badge-blue',
        note: `Modified sections: ${changeSectionsStr}${changeTagsStr}. Note: ${finalNote}`,
      });
    }

    const finalModifiedSections =
      extraData.modifiedSections ||
      formData.modifiedSections ||
      initialCampaign?.governance?.modifiedSections ||
      initialCampaign?.modifiedSections ||
      (extraData.isMemberChangeRequest
        ? ['Step 1: Brief & Goals', 'Step 2: Target Cohorts & Leads']
        : null);

    const finalChangeReasonTags =
      extraData.changeReasonTags ||
      formData.changeReasonTags ||
      initialCampaign?.governance?.changeReasonTags ||
      initialCampaign?.changeReasonTags ||
      (extraData.isMemberChangeRequest ? ['Targeting & ICP Refinement'] : null);

    const finalChangeRequestedBy =
      extraData.changeRequestedBy ||
      formData.changeRequestedBy ||
      initialCampaign?.governance?.changeRequestedBy ||
      initialCampaign?.changeRequestedBy ||
      (extraData.isMemberChangeRequest ? currentActorStr : null);

    const finalChangeRequestedAt =
      extraData.changeRequestedAt ||
      formData.changeRequestedAt ||
      initialCampaign?.governance?.changeRequestedAt ||
      initialCampaign?.changeRequestedAt ||
      (extraData.isMemberChangeRequest ? nowIso : null);

    const governanceBlock = {
      gates: Array.from(
        approvedGates.size > 0
          ? approvedGates
          : isNowApprovedOrActive
            ? ['brand_voice', 'commercial', 'compliance_deliverability', 'pdpa_privacy']
            : []
      ),
      allGatesApproved: isNowApprovedOrActive,
      isCertified: isNowApprovedOrActive,
      signatureType: finalSignatureType,
      signatureData: finalSignatureData,
      typedSignature: finalTypedSignature,
      signedBy: finalSignedBy,
      certId: isNowApprovedOrActive ? certId : null,
      certHash: isNowApprovedOrActive ? certHash : null,
      securityStandard: 'HMAC-SHA256 (PDPA Compliance)',
      approvedAt:
        approvedAt ||
        initialCampaign?.governance?.approvedAt ||
        (isNowApprovedOrActive ? new Date().toISOString() : null),
      originalApprovedBy:
        initialCampaign?.governance?.originalApprovedBy ||
        initialCampaign?.approvedBy ||
        (actionType === 'DIRECT_APPROVE' || actionType === 'DIRECT_ACTIVATE'
          ? currentActorStr
          : null),
      originalApprovedAt:
        initialCampaign?.governance?.originalApprovedAt ||
        initialCampaign?.approvedAt ||
        (actionType === 'DIRECT_APPROVE' || actionType === 'DIRECT_ACTIVATE' ? nowIso : null),
      approverRole:
        isNowApprovedOrActive && canDirectApprove
          ? userRoleLabel || 'Sales Manager'
          : initialCampaign?.governance?.approverRole ||
            (String(approvedBy || finalSignedBy).match(/\((.*?)\)/) || [])[1] ||
            'Sales Manager',
      pausedBy,
      pausedAt,
      pauseRequestedBy,
      pauseRequestedAt,
      modifiedSections: finalModifiedSections,
      changeReasonTags: finalChangeReasonTags,
      changeRequestedBy: finalChangeRequestedBy,
      changeRequestedAt: finalChangeRequestedAt,
      history: auditHistory,
    };

    const savedCampaign = {
      ...(initialCampaign || {}),
      id: initialCampaign?.id || undefined,
      name: formData.title || 'Untitled AI Campaign',
      objective: formData.primaryObjective,
      product: formData.product || 'AI Lead Generation',
      productDescription: formData.productDescription || '',
      campaignType: formData.campaignType,
      industrySector: formData.industrySector,
      targetMarket: formData.targetRegion,
      targetCompanyType: `${formData.industrySector} (${formData.targetRegion})`,
      targetPersonas: formData.targetPersonas.length > 0 ? formData.targetPersonas : ['VP Sales'],
      channels: formData.channels,
      startDate: formData.startDate,
      endDate: formData.endDate,
      durationDays: formData.durationDays,
      currentDay:
        actionType === 'DIRECT_ACTIVATE' ||
        (status === 'ACTIVE' && (!initialCampaign || initialCampaign.currentDay === 0))
          ? 1
          : initialCampaign?.currentDay || 0,
      status,
      pausedBy,
      pausedAt,
      pauseRequestedBy,
      pauseRequestedAt,
      audienceCount: calculatedKPIs.audience,
      expectedReplies: calculatedKPIs.replies,
      expectedMeetings: calculatedKPIs.meetings,
      expectedPipeline: calculatedKPIs.pipelineValue,
      budget: formData.allocatedBudget,
      targetLeads: formData.importedLeads || [],
      importedLeads: formData.importedLeads || [],
      createdAt: initialCampaign?.createdAt || formatLocalDate(new Date()),
      createdBy: initialCampaign?.createdBy || `${currentUserName} (${userRoleLabel})`,
      updatedAt: new Date().toISOString(),
      approvedBy,
      approvedAt,
      submissionNotes:
        extraData.submissionNotes ||
        extraData.revisionFeedback ||
        formData.submissionNotes ||
        'AI-generated outbound strategy configuration.',
      modifiedSections: finalModifiedSections,
      changeReasonTags: finalChangeReasonTags,
      changeRequestedBy: finalChangeRequestedBy,
      changeRequestedAt: finalChangeRequestedAt,
      revisionFeedback:
        extraData.revisionFeedback ||
        formData.revisionFeedback ||
        initialCampaign?.revisionFeedback ||
        (actionType === 'REQUEST_CHANGES' ? formData.submissionNotes : null),
      revisionRequestedBy:
        actionType === 'REQUEST_CHANGES'
          ? `${currentUserName} (${userRoleLabel})`
          : initialCampaign?.revisionRequestedBy || null,
      revisionRequestedAt:
        actionType === 'REQUEST_CHANGES'
          ? new Date().toISOString()
          : initialCampaign?.revisionRequestedAt || null,
      governance: governanceBlock,
      strategy: {
        startDate: formData.startDate,
        endDate: formData.endDate,
        positioning: formData.positioning,
        painPoints: formData.painPoints,
        matrix: formData.matrix,
        journey: formData.journey,
        schedule: formData.schedule || DEFAULT_TIMING_CONFIG,
        importedLeads: formData.importedLeads || [],
        modifiedSections: finalModifiedSections,
        changeReasonTags: finalChangeReasonTags,
        changeRequestedBy: finalChangeRequestedBy,
        changeRequestedAt: finalChangeRequestedAt,
        kpiForecast: {
          audience: calculatedKPIs.audience,
          reachable: calculatedKPIs.reachable,
          replies: calculatedKPIs.replies,
          positiveReplies: calculatedKPIs.positiveReplies,
          meetings: calculatedKPIs.meetings,
          opportunities: calculatedKPIs.opportunities,
          pipelineValue: calculatedKPIs.pipelineValue,
          pipelineRange: calculatedKPIs.pipelineRange,
          avgDealSize: parseCurrencyNumber(formData.avgDealSize) || 0,
          allocatedBudget: formData.allocatedBudget,
          roiMultiple: calculatedKPIs.financials?.roiMultiple,
          costPerMeeting: calculatedKPIs.financials?.costPerMeeting,
          costPerOpportunity: calculatedKPIs.financials?.costPerOpportunity,
          rates: calculatedKPIs.rates,
          rationale: kpiAiRationale,
        },
      },
    };

    setIsSaving(true);
    setValidationError('');
    try {
      let savedResult = null;
      if (onSaveCampaign) {
        savedResult = await onSaveCampaign(savedCampaign, actionType);
      }
      setIsEditing(false);
      setHasUnsavedChanges(false);
      const rolesToUse = getRolesToUse();
      const currentFingerprint = computeCurrentLeadFingerprint(rolesToUse);
      lastSyncedLeadFingerprintRef.current = currentFingerprint;
      acknowledgedFingerprintsRef.current.add(currentFingerprint);

      // If directly activating and Step 1 channel is Email, WhatsApp, or LinkedIn, open outreach modal
      if (actionType === 'DIRECT_ACTIVATE') {
        if (savedResult) {
          setFormData((prev) => ({
            ...prev,
            id: savedResult.id || prev.id,
            status: savedResult.status || 'ACTIVE',
            governance: savedResult.governance || prev.governance,
          }));
        }

        const firstTouchpoint = (formData.journey &&
          formData.journey.length > 0 &&
          formData.journey[0]) ||
          (savedResult?.strategy?.journey &&
            savedResult.strategy.journey.length > 0 &&
            savedResult.strategy.journey[0]) || {
            step: 1,
            day: 1,
            channel: formData.channels?.some((c) => /linkedin/i.test(c))
              ? 'LinkedIn'
              : formData.channels?.some((c) => /whatsapp/i.test(c))
                ? 'WhatsApp'
                : 'Email',
            name: 'Introduction & Value Proposition Hook',
            condition: 'Initial Touch',
          };

        const rawChannel = (
          firstTouchpoint?.channel ||
          formData.channels?.find((c) => /linkedin|whatsapp|email/i.test(c)) ||
          'Email'
        ).toLowerCase();

        const isEmail = rawChannel.includes('email');
        const isWhatsApp = rawChannel.includes('whatsapp');
        const isLinkedIn = rawChannel.includes('linkedin');
        const isPhone = rawChannel.includes('phone') || rawChannel.includes('call');

        if (isEmail || isWhatsApp || isLinkedIn || isPhone) {
          const recipientsList =
            formData.importedLeads && formData.importedLeads.length > 0
              ? formData.importedLeads
              : savedResult?.targetLeads && savedResult.targetLeads.length > 0
                ? savedResult.targetLeads
                : savedResult?.importedLeads || [];

          setOutreachLauncherState({
            isOpen: true,
            channel: isPhone
              ? 'Phone Call'
              : isLinkedIn
                ? 'LinkedIn'
                : isWhatsApp
                  ? 'WhatsApp'
                  : 'Email',
            touchpoint: firstTouchpoint,
            campaign: savedResult || savedCampaign,
            recipients: recipientsList,
            isPostCreation: true,
          });
          return;
        }
      }
    } catch (err) {
      console.error('[CreateCampaign] Error saving campaign:', err);
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to save campaign to database.';
      setValidationError(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLaunchTouchpointOutreach = (touchpoint) => {
    const maxTouches = Number(
      formData.schedule?.maxCampaignTouches ||
        formData.timingConfig?.maxCampaignTouches ||
        formData.strategy?.schedule?.maxCampaignTouches
    );
    if (maxTouches > 0 && touchpoint?.step > maxTouches) {
      alert(
        `Cannot launch Touchpoint ${touchpoint.step}: This touchpoint exceeds the Max Campaign Touches limit (${maxTouches}) configured for this campaign.`
      );
      return;
    }

    const rawChannel = (touchpoint?.channel || 'Email').toLowerCase();
    const isPhone = rawChannel.includes('phone') || rawChannel.includes('call');
    const isLinkedIn = rawChannel.includes('linkedin');
    const isWhatsApp = rawChannel.includes('whatsapp');
    const recipientsList =
      formData.importedLeads && formData.importedLeads.length > 0
        ? formData.importedLeads
        : formData.targetLeads && formData.targetLeads.length > 0
          ? formData.targetLeads
          : formData.strategy?.importedLeads && formData.strategy.importedLeads.length > 0
            ? formData.strategy.importedLeads
            : formData.strategy?.targetLeads && formData.strategy.targetLeads.length > 0
              ? formData.strategy.targetLeads
              : formData.leads && formData.leads.length > 0
                ? formData.leads
                : [];

    // Filter recipients: prioritize touchpoint.eligibleLeads or exclude leads who already replied/stopped
    const filteredRecipients = Array.isArray(touchpoint?.eligibleLeads)
      ? touchpoint.eligibleLeads
      : recipientsList.filter((lead) => {
          const stage = String(lead?.stage || '').toLowerCase();
          if (stage === 'lost' || stage === 'qualified') {
            return false;
          }
          if (stage === 'new') {
            return true;
          }
          const keys = [lead.id, lead._id, lead.email, lead.name].filter(Boolean).map(String);
          const journey = formData.journey || [];
          for (const step of journey) {
            const stepResponses = step.leadResponses || {};
            for (const k of keys) {
              const resp = stepResponses[k];
              if (
                resp &&
                (resp.responseType === 'interested' ||
                  resp.responseType === 'positive' ||
                  resp.responseType === 'not_interested' ||
                  resp.responseType === 'negative')
              ) {
                return false;
              }
            }
          }
          return true;
        });

    setOutreachLauncherState({
      isOpen: true,
      channel: isPhone ? 'Phone Call' : isLinkedIn ? 'LinkedIn' : isWhatsApp ? 'WhatsApp' : 'Email',
      touchpoint,
      campaign: {
        ...formData,
        name: formData.title || 'Outbound Campaign',
      },
      recipients: filteredRecipients,
      isPostCreation: false,
    });
  };

  const handlePersistTouchpointStatus = async (stepIdx, patchData) => {
    const activeCampaignId =
      formData.id || outreachLauncherState.campaign?.id || initialCampaign?.id;

    // Apply leadUpdates locally to formData.importedLeads if provided
    if (patchData?.leadUpdates) {
      setFormData((prev) => {
        const updatesList = Array.isArray(patchData.leadUpdates)
          ? patchData.leadUpdates
          : Object.entries(patchData.leadUpdates).map(([k, v]) => ({ leadKey: k, ...v }));

        const updateList = (list) => {
          if (!Array.isArray(list)) return list;
          let updated = [...list];
          updatesList.forEach((up) => {
            const matchKey = String(up.leadKey || up.leadId || up.id || '');
            updated = updated.map((l) => {
              const keys = [l.id, l._id, l.email, l.name].filter(Boolean).map(String);
              if (keys.includes(matchKey)) {
                return {
                  ...l,
                  ...(up.stage !== undefined ? { stage: up.stage } : {}),
                  ...(up.assignee !== undefined ? { assignee: up.assignee } : {}),
                  ...(up.assigneeName !== undefined ? { assigneeName: up.assigneeName } : {}),
                  ...(up.tasks !== undefined ? { tasks: up.tasks } : {}),
                  ...(up.lostReason !== undefined ? { lostReason: up.lostReason } : {}),
                  ...(up.leadResponse !== undefined ? { leadResponse: up.leadResponse } : {}),
                };
              }
              return l;
            });
          });
          return updated;
        };

        const next = {
          ...prev,
          ...(prev.importedLeads ? { importedLeads: updateList(prev.importedLeads) } : {}),
          ...(prev.targetLeads ? { targetLeads: updateList(prev.targetLeads) } : {}),
        };
        if (next.strategy) {
          next.strategy = {
            ...next.strategy,
            ...(next.strategy.importedLeads
              ? { importedLeads: updateList(next.strategy.importedLeads) }
              : {}),
            ...(next.strategy.targetLeads
              ? { targetLeads: updateList(next.strategy.targetLeads) }
              : {}),
          };
        }
        return next;
      });
    }

    if (patchData?.assignedMembers) {
      setFormData((prev) => ({
        ...prev,
        assignedMembers: patchData.assignedMembers,
        strategy: {
          ...(prev.strategy || {}),
          assignedMembers: patchData.assignedMembers,
        },
      }));
    }

    if (!activeCampaignId) return;

    try {
      const payload = {
        stepIndex: stepIdx,
        ...patchData,
      };
      const res = await axios.patch(`/campaign/${activeCampaignId}/touchpoint-status`, payload);
      if (res.data?.success && res.data.data) {
        const updated = res.data.data;
        if (typeof onCampaignDispatchedUpdate === 'function') {
          onCampaignDispatchedUpdate(updated);
        }
      }
    } catch (err) {
      console.error('[CreateCampaign] Failed to persist touchpoint status to database:', err);
    }
  };

  const handleUpdateTouchpointDispatched = (touchpoint, deliveryMap) => {
    if (!touchpoint || !deliveryMap) return;

    let computedStatus;
    let stepIndexFound = -1;
    const newlyContactedKeys = new Set();
    const newlyContactedLeads = [];
    const leadUpdates = {};

    setFormData((prev) => {
      const stepIdx = prev.journey.findIndex(
        (j) =>
          j.step === touchpoint.step ||
          (touchpoint.id && j.id === touchpoint.id) ||
          (touchpoint.name && j.name === touchpoint.name)
      );
      if (stepIdx === -1) return prev;
      stepIndexFound = stepIdx;

      const updatedJourney = [...prev.journey];
      const targetStep = { ...updatedJourney[stepIdx] };
      const mergedStatus = {
        ...(targetStep.leadOutreachStatus || {}),
        ...deliveryMap,
      };
      targetStep.leadOutreachStatus = mergedStatus;

      // Check if all leads have been sent to auto-mark status
      const effectiveLeads =
        prev.importedLeads?.length > 0
          ? prev.importedLeads
          : prev.targetLeads?.length > 0
            ? prev.targetLeads
            : prev.strategy?.importedLeads?.length > 0
              ? prev.strategy.importedLeads
              : prev.strategy?.targetLeads?.length > 0
                ? prev.strategy.targetLeads
                : prev.leads?.length > 0
                  ? prev.leads
                  : [];
      const totalLeads = effectiveLeads.length;
      if (totalLeads > 0) {
        const sentCount = effectiveLeads.filter((lead) => {
          const keys = [lead.id, lead._id, lead.email, lead.name]
            .filter((k) => k !== undefined && k !== null && String(k).trim() !== '')
            .map(String);
          return keys.some((k) => mergedStatus[k]?.sent === true);
        }).length;
        if (sentCount >= totalLeads) {
          targetStep.status = 'completed';
          computedStatus = 'completed';
        }
      }

      // Identify newly contacted leads to update their stage to 'Contacted'
      effectiveLeads.forEach((lead) => {
        const keys = [lead.id, lead._id, lead.email, lead.name]
          .filter((k) => k !== undefined && k !== null && String(k).trim() !== '')
          .map(String);
        const isDispatched = keys.some((k) => deliveryMap[k]?.sent === true);
        const currentStage = String(lead.stage || '').toLowerCase();
        if (isDispatched && (!lead.stage || currentStage === 'new')) {
          newlyContactedLeads.push(lead);
          keys.forEach((k) => {
            newlyContactedKeys.add(k);
            leadUpdates[k] = { stage: 'Contacted' };
          });
        }
      });

      const updateList = (list) => {
        if (!Array.isArray(list) || newlyContactedKeys.size === 0) return list;
        return list.map((l) => {
          const keys = [l.id, l._id, l.email, l.name]
            .filter((k) => k !== undefined && k !== null && String(k).trim() !== '')
            .map(String);
          if (keys.some((k) => newlyContactedKeys.has(k))) {
            return { ...l, stage: 'Contacted' };
          }
          return l;
        });
      };

      updatedJourney[stepIdx] = targetStep;
      return {
        ...prev,
        journey: updatedJourney,
        importedLeads: updateList(prev.importedLeads),
        targetLeads: updateList(prev.targetLeads),
        strategy: {
          ...(prev.strategy || {}),
          journey: updatedJourney,
          ...(prev.strategy?.importedLeads
            ? { importedLeads: updateList(prev.strategy.importedLeads) }
            : {}),
          ...(prev.strategy?.targetLeads
            ? { targetLeads: updateList(prev.strategy.targetLeads) }
            : {}),
        },
      };
    });

    // Direct CRM sync for newly contacted leads
    newlyContactedLeads.forEach((lead) => {
      const crmId = lead.pipelineLeadId || lead.id || lead._id;
      if (crmId) {
        axios.patch(`/lead-pipeline/${crmId}`, { stage: 'Contacted' }).catch((err) => {
          console.warn('[CreateCampaign] Failed to sync Contacted stage to pipeline:', err);
        });
      }
    });

    setOutreachLauncherState((prev) => {
      if (!prev.isOpen || !prev.touchpoint) return prev;
      return {
        ...prev,
        touchpoint: {
          ...prev.touchpoint,
          leadOutreachStatus: {
            ...(prev.touchpoint.leadOutreachStatus || {}),
            ...deliveryMap,
          },
        },
      };
    });

    // Directly persist to database if campaign exists
    const campaignId = formData.id || outreachLauncherState.campaign?.id || initialCampaign?.id;

    if (campaignId && (stepIndexFound >= 0 || touchpoint.step !== undefined)) {
      handlePersistTouchpointStatus(stepIndexFound >= 0 ? stepIndexFound : undefined, {
        stepNumber: touchpoint.step,
        leadOutreachStatus: deliveryMap,
        ...(computedStatus ? { status: computedStatus } : {}),
        ...(Object.keys(leadUpdates).length > 0 ? { leadUpdates } : {}),
      });
    }
  };

  const filteredModalLeads = useMemo(() => {
    const list = pipelineLeads.filter((lead) => {
      if (modalStageFilter !== 'ALL' && lead.stage !== modalStageFilter) return false;
      if (modalScoreFilter === 'HIGH' && lead.leadScore < 80) return false;
      if (modalScoreFilter === 'MED' && (lead.leadScore < 60 || lead.leadScore >= 80)) return false;
      if (modalSearch.trim()) {
        const q = modalSearch.toLowerCase().trim();
        const matchName = lead.name && lead.name.toLowerCase().includes(q);
        const matchCompany = lead.company && lead.company.toLowerCase().includes(q);
        const matchTitle = lead.jobTitle && lead.jobTitle.toLowerCase().includes(q);
        const matchIndustry = lead.industry && lead.industry.toLowerCase().includes(q);
        const matchCountry = lead.country && lead.country.toLowerCase().includes(q);
        const matchEmail = lead.email && lead.email.toLowerCase().includes(q);
        if (
          !matchName &&
          !matchCompany &&
          !matchTitle &&
          !matchIndustry &&
          !matchCountry &&
          !matchEmail
        ) {
          return false;
        }
      }
      return true;
    });

    // Sort: Selectable leads at the top, locked leads (in other active campaigns) at the bottom
    return list.sort((a, b) => {
      const aLocked = a.inOtherCampaign ? 1 : 0;
      const bLocked = b.inOtherCampaign ? 1 : 0;
      if (aLocked !== bLocked) {
        return aLocked - bLocked;
      }
      return (Number(b.leadScore) || 0) - (Number(a.leadScore) || 0);
    });
  }, [pipelineLeads, modalStageFilter, modalScoreFilter, modalSearch]);

  return (
    <div className="create-campaign-container">
      {/* ── Top Navigation & Breadcrumb Bar ── */}
      <div className="cmp-nav-breadcrumb-bar">
        <div className="cmp-nav-breadcrumb-left">
          <button className="cmp-nav-back-btn" onClick={handleBackToCatalog}>
            <i className="fas fa-arrow-left" /> Back to Campaigns Catalog
          </button>
        </div>
        <div className="cmp-nav-breadcrumb-right">
          {isExistingCampaign && (
            <>
              {/* When in Read-Only / View Mode (disabled when Pending Approval) */}
              {!isEditing && initialCampaign?.status !== 'PENDING_APPROVAL' && (
                <button
                  type="button"
                  className="cmp-btn-edit-trigger"
                  onClick={() => setIsEditing(true)}
                  title="Unlock campaign fields and modify strategy"
                >
                  <i className="fas fa-edit" /> Edit Campaign
                </button>
              )}

              {/* When in Active Editing Mode */}
              {isEditing && (
                <div className="cmp-nav-edit-actions-group">
                  <button
                    type="button"
                    className="cmp-btn-cancel-quick"
                    onClick={handleCancelEditing}
                    title="Cancel editing and revert modifications"
                  >
                    <i className="fas fa-times" /> Cancel
                  </button>
                  {canDirectApprove ? (
                    <button
                      type="button"
                      className="cmp-btn-save-quick"
                      disabled={isSaving}
                      onClick={() => handleFinalize('SAVE_CHANGES')}
                      title="Save all campaign modifications directly"
                    >
                      {isSaving ? (
                        <>
                          <i className="fas fa-spinner fa-spin" /> Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save" /> Save Changes
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="cmp-btn-submit-change-request-quick"
                      disabled={isSaving}
                      onClick={() => setIsMemberChangeRequestModalOpen(true)}
                      title={
                        initialCampaign?.status === 'CHANGES_REQUESTED'
                          ? 'Save and resubmit your revised strategy for manager approval'
                          : 'Submit your modified strategy and changed content for Manager / Admin approval'
                      }
                    >
                      <i className="fas fa-paper-plane" />{' '}
                      {initialCampaign?.status === 'CHANGES_REQUESTED'
                        ? 'Resubmit Revisions'
                        : 'Submit Change Request'}
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          <span>
            Workspace: <strong>{workspaceName}</strong>
          </span>
          <span className="cmp-role-pill">{userRoleLabel}</span>
        </div>
      </div>

      {/* ── Governance Approval Required Banner (AC-06) ── */}
      {isEditMode && initialCampaign?.status === 'PENDING_APPROVAL' && (
        <GovernanceApprovalRequiredBanner
          campaign={initialCampaign}
          formData={formData}
          approvedGates={approvedGates}
          onToggleGate={handleToggleGate}
          onRequestChanges={handleRequestChanges}
          onApproveNow={() => setWizardStep(6)}
          onOpenExecutiveSignModal={() => setWizardStep(6)}
          canDirectApprove={canDirectApprove}
        />
      )}

      {/* ── Changes Requested / Revision Required Banner ── */}
      {isExistingCampaign && initialCampaign?.status === 'CHANGES_REQUESTED' && (
        <div className="cmp-revision-requested-banner cmp-rev-notice-card">
          <div className="cmp-rev-notice-left">
            <div className="cmp-rev-notice-icon">
              <i className="fas fa-history" />
            </div>
            <div className="cmp-rev-notice-content">
              <div className="cmp-rev-notice-header">
                <span className="cmp-rev-notice-title">
                  {canDirectApprove
                    ? `Changes Requested for ${initialCampaign.createdBy || 'Sales Member'}`
                    : `Revisions Requested by ${initialCampaign.revisionRequestedBy || 'Sales Manager'}`}
                </span>
                <span className="cmp-rev-notice-badge">
                  {canDirectApprove ? 'Awaiting Resubmission' : 'Action Required'}
                </span>
              </div>

              <div className="cmp-rev-notice-body">
                {parsedRevision.tag && (
                  <span className="cmp-rev-tag-chip">
                    <i className="fas fa-tag" /> {parsedRevision.tag}
                  </span>
                )}
                <span className="cmp-rev-note-text">
                  {parsedRevision.message ||
                    (canDirectApprove
                      ? 'Revisions requested from proposer. Awaiting member updates.'
                      : 'Please review and update the strategy according to feedback.')}
                </span>
              </div>
            </div>
          </div>

          <div className="cmp-rev-notice-right">
            {!isEditing ? (
              canDirectApprove ? (
                <button
                  type="button"
                  className="cmp-rev-btn-action outline"
                  onClick={() => {
                    setIsEditing(true);
                    setWizardStep(1);
                  }}
                  title="Directly edit and adjust this campaign as Manager/Owner"
                >
                  <i className="fas fa-edit" /> Direct Edit
                </button>
              ) : (
                <button
                  type="button"
                  className="cmp-rev-btn-action primary"
                  onClick={() => {
                    setIsEditing(true);
                    setWizardStep(1);
                  }}
                >
                  <i className="fas fa-edit" /> Edit Strategy
                </button>
              )
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  type="button"
                  className="cmp-btn-secondary"
                  onClick={() => handleFinalize('SAVE_CHANGES')}
                  style={{ padding: '6px 12px', fontSize: '12.5px', borderRadius: '6px' }}
                >
                  <i className="fas fa-save" /> Save Draft
                </button>
                <button
                  type="button"
                  className="cmp-btn-primary"
                  onClick={() => {
                    if (canDirectApprove) {
                      handleFinalize('DIRECT_APPROVE');
                    } else {
                      setIsMemberChangeRequestModalOpen(true);
                    }
                  }}
                  style={{ padding: '6px 14px', fontSize: '12.5px', borderRadius: '6px' }}
                >
                  <i className={canDirectApprove ? 'fas fa-check-circle' : 'fas fa-paper-plane'} />{' '}
                  {canDirectApprove ? 'Approve & Certify' : 'Save & Resubmit'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Pause Request Reminder Banner (Visible Across All Tabs) ── */}
      {isExistingCampaign &&
        initialCampaign?.status === 'ACTIVE' &&
        Boolean(
          initialCampaign?.pauseRequestedBy || initialCampaign?.governance?.pauseRequestedBy
        ) && (
          <div className="cmp-pause-request-alert-banner">
            <div className="cmp-pause-banner-left">
              <div className="cmp-pause-banner-icon">
                <i className="fas fa-hand-paper" />
              </div>
              <div className="cmp-pause-banner-text">
                <div className="cmp-pause-banner-title">
                  <strong>
                    Pause Request from{' '}
                    {initialCampaign.pauseRequestedBy ||
                      initialCampaign.governance?.pauseRequestedBy}
                  </strong>
                  <span className="cmp-status-inline-tag pause-requested">
                    Pause Approval Pending
                  </span>
                </div>
                <p className="cmp-pause-banner-notes">
                  A team member has requested to pause this active campaign. Management decision is
                  required.
                </p>
              </div>
            </div>
            <div className="cmp-pause-banner-actions">
              {canDirectApprove && pauseCheck.canApprove ? (
                <>
                  <button
                    type="button"
                    className="cmp-btn-pause-reject"
                    disabled={isSaving}
                    onClick={() => handleFinalize('REJECT_PAUSE_REQUEST')}
                    title="Reject pause request and keep campaign active"
                  >
                    <i className="fas fa-times" /> Reject Request
                  </button>
                  <button
                    type="button"
                    className="cmp-btn-pause-approve"
                    disabled={isSaving}
                    onClick={() => handleFinalize('PAUSE')}
                    title={
                      pauseCheck.isSuperior
                        ? `Direct Approve: Override & Pause Campaign (${userRoleLabel})`
                        : `Approve & Pause Campaign (${currentUserName})`
                    }
                  >
                    <i className="fas fa-pause" /> Approve &amp; Pause
                  </button>
                </>
              ) : (
                <span className="cmp-pause-banner-member-status">
                  <i className="fas fa-clock" />{' '}
                  {canDirectApprove && !pauseCheck.canApprove
                    ? `Requires ${pauseCheck.originalSignatoryRole || 'Admin/Owner'} Approval`
                    : 'Awaiting Manager Decision'}
                </span>
              )}
              {wizardStep !== 6 && (
                <button
                  type="button"
                  className="cmp-btn-pause-goto-step6"
                  onClick={() => setWizardStep(6)}
                >
                  Step 6 Governance <i className="fas fa-arrow-right" />
                </button>
              )}
            </div>
          </div>
        )}

      {/* ── CRM Role Sync Reminder Banner (Visible at top across tabs until dismissed/synced) ── */}
      {Boolean(
        formData.matrix &&
        formData.matrix.length > 0 &&
        (formData.importedLeads?.length > 0 || formData.targetPersonas?.length > 0) &&
        checkRolesOutOfSync(getRolesToUse(), formData.matrix)
      ) &&
        !isRoleSyncBannerDismissed && (
          <div
            className="cmp-pause-request-alert-banner cmp-sync-reminder-banner"
            style={{ borderLeftColor: '#f59e0b', marginBottom: '16px' }}
          >
            <div className="cmp-pause-banner-left">
              <div
                className="cmp-pause-banner-icon"
                style={{ background: '#fef3c7', color: '#b45309' }}
              >
                <i className="fas fa-sync-alt" />
              </div>
              <div className="cmp-pause-banner-text">
                <div className="cmp-pause-banner-title">
                  <strong>CRM Lead Roles Updated</strong>
                  <span
                    className="cmp-status-inline-tag pause-requested"
                    style={{ background: '#fef3c7', color: '#92400e', borderColor: '#fde68a' }}
                  >
                    Sync Available
                  </span>
                </div>
                <p className="cmp-pause-banner-notes" style={{ margin: 0 }}>
                  {updatedLeadRolesSummary
                    ? `${updatedLeadRolesSummary}. Synchronize Tab 3 messaging matrix to match.`
                    : 'Decision-maker roles in your campaign leads differ from the current Messaging Matrix (Tab 3).'}
                </p>
              </div>
            </div>
            <div
              className="cmp-pause-banner-actions"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <button
                type="button"
                className="cmp-btn-primary"
                disabled={isAnyAiGenerating}
                style={{
                  padding: '6px 14px',
                  fontSize: '12.5px',
                  background: '#d97706',
                  borderColor: '#d97706',
                  color: '#ffffff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: isAnyAiGenerating ? 0.75 : 1,
                  cursor: isAnyAiGenerating ? 'not-allowed' : 'pointer',
                }}
                onClick={() => {
                  if (isAnyAiGenerating) return;
                  setPendingTargetStep(3);
                  setIsAiGeneratePromptModalOpen(true);
                }}
              >
                {isAnyAiGenerating ? (
                  <>
                    <i className="fas fa-spinner fa-spin" /> Syncing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sync-alt" /> Sync Strategy
                  </>
                )}
              </button>
              <button
                type="button"
                className="cmp-banner-dismiss-btn"
                disabled={isAnyAiGenerating}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: isAnyAiGenerating ? 'not-allowed' : 'pointer',
                  padding: '6px 8px',
                  fontSize: '14px',
                  color: '#92400e',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: isAnyAiGenerating ? 0.5 : 1,
                }}
                onClick={() => {
                  if (isAnyAiGenerating) return;
                  setIsRoleSyncBannerDismissed(true);
                }}
                title="Dismiss reminder"
              >
                <i className="fas fa-times" />
              </button>
            </div>
          </div>
        )}

      {/* ── TOP 6-STEP TABS ── */}
      <WizardStepper
        wizardStep={wizardStep}
        onStepClick={handleStepClick}
        isEditMode={isEditMode}
        maxStepReached={maxStepReached}
        campaignStatus={initialCampaign?.status}
        isStepValid={(stepNum) => isStepCompleted(stepNum)}
        isDisabled={isBlockingNavigation}
        stepGeneratingMap={stepGeneratingMap}
      />

      {/* ── MAIN IN-PAGE CARD CONTAINER ── */}
      <div className={`cmp-blueprint-main-card ${isAnyAiGenerating ? 'is-ai-generating' : ''}`}>
        {/* Step Validation Error Notice */}
        {validationError && (
          <div className="cmp-alert danger cmp-wizard-validation-alert">
            <i className="fas fa-exclamation-circle" />
            <div className="cmp-alert-msg-wrap">
              <strong>Please complete required step requirements:</strong>
              <p>{validationError}</p>
            </div>
            <button
              type="button"
              className="cmp-alert-close-btn"
              onClick={() => setValidationError('')}
              title="Dismiss warning"
            >
              <i className="fas fa-times" />
            </button>
          </div>
        )}

        {/* ── STEP 1: BRIEF & GOALS ── */}
        {wizardStep === 1 && (
          <Step1BriefGoals
            formData={formData}
            setFormData={setFormData}
            validationError={validationError}
            setValidationError={setValidationError}
            isReadOnly={isReadOnly}
            isGeneratingBlueprint={isAnyAiGenerating}
            setIsGeneratingBlueprint={setIsGeneratingBlueprint}
            onApplyPlaybook={handleApplyPlaybook}
            onTogglePersona={handleTogglePersona}
            onToggleChannel={handleToggleChannel}
          />
        )}

        {/* ── STEP 2: TARGET COHORT ── */}
        {wizardStep === 2 && (
          <Step2TargetCohort
            formData={formData}
            isReadOnly={isReadOnly}
            isGeneratingAi={isAnyAiGenerating}
            onRegenerateAudience={handleRegenerateAudience}
            onOpenImportModal={handleOpenImportModal}
            onEnrichAndAddLushaLeads={handleEnrichAndAddLushaLeads}
            importedSearch={importedSearch}
            setImportedSearch={setImportedSearch}
            selectedImportedIds={selectedImportedIds}
            onToggleSelectImported={handleToggleSelectImported}
            onToggleSelectAllImported={handleToggleSelectAllImported}
            onDeleteSelectedImported={handleDeleteSelectedImported}
            onRemoveImportedLead={handleRemoveImportedLead}
          />
        )}

        {/* ── STEP 3: OFFER & MATRIX ── */}
        {wizardStep === 3 && (
          <Step3OfferMatrix
            formData={formData}
            setFormData={setFormData}
            isReadOnly={isReadOnly}
            isGeneratingMatrix={isAnyAiGenerating || isGeneratingMatrix}
            generatingCardIndex={generatingCardIndex}
            activeAngle={activeAngle}
            hasGeneratedMatrix={hasGeneratedMatrix}
            onRegenerateMatrix={handleRegenerateMatrix}
            onRegenerateSingleCard={handleRegenerateSingleCard}
            onUpdateMatrixRow={handleUpdateMatrixRow}
            onRemoveMatrixRow={handleRemoveMatrixRow}
            onAddMatrixRole={handleAddMatrixRole}
          />
        )}

        {/* ── STEP 4: OUTREACH CADENCE ── */}
        {wizardStep === 4 && (
          <Step4TeamAssign
            formData={formData}
            setFormData={setFormData}
            isReadOnly={isReadOnly}
            isCreateMode={!isEditMode && !formData.id}
            hasGeneratedSequence={hasGeneratedSequence}
            onGeneratedSequence={() => setHasGeneratedSequence(true)}
            isGeneratingSequence={isAnyAiGenerating || isGeneratingSequence}
            setIsGeneratingSequence={setIsGeneratingSequence}
            onAddJourneyStep={handleAddJourneyStep}
            onUpdateJourneyStep={handleUpdateJourneyStep}
            onRemoveJourneyStep={handleRemoveJourneyStep}
            onLaunchOutreach={handleLaunchTouchpointOutreach}
            onPersistTouchpointStatus={(stepIdx, patchData) =>
              handlePersistTouchpointStatus(stepIdx, patchData)
            }
          />
        )}

        {/* ── STEP 5: FORECAST & KPIS ── */}
        {wizardStep === 5 && (
          <Step5TasksBudget
            formData={formData}
            setFormData={setFormData}
            calculatedKPIs={calculatedKPIs}
            isReadOnly={isReadOnly}
            onSimulateKpis={handleSimulateKpis}
            isSimulatingKpis={isAnyAiGenerating || isSimulatingKpis}
            aiRationale={kpiAiRationale}
          />
        )}

        {/* ── STEP 6: GOVERNANCE & APPROVAL ── */}
        {wizardStep === 6 && (
          <Step6Governance
            formData={formData}
            calculatedKPIs={calculatedKPIs}
            currentUserName={currentUserName}
            userRoleLabel={userRoleLabel}
            workspaceName={workspaceName}
            canDirectApprove={canDirectApprove}
            isEditMode={isEditMode}
            isReadOnly={isReadOnly}
            initialCampaign={initialCampaign}
            approvedGates={approvedGates}
            onToggleGate={handleToggleGate}
            onRequestChanges={handleRequestChanges}
            signatureMode={signatureMode}
            setSignatureMode={setSignatureMode}
            typedSignature={typedSignature}
            setTypedSignature={setTypedSignature}
            hasDrawnSignature={hasDrawnSignature}
            isPolicyCertified={isPolicyCertified}
            setIsPolicyCertified={setIsPolicyCertified}
            isScanning={isScanning || isAnyAiGenerating}
            signatureCanvasRef={signatureCanvasRef}
            onStartDraw={handleStartDraw}
            onDraw={handleDraw}
            onStopDraw={handleStopDraw}
            onClearSignature={handleClearSignature}
            onTriggerBiometricScan={handleTriggerBiometricScan}
            onFinalize={handleFinalize}
            onNavigateToStep={setWizardStep}
            isSaving={isSaving || isAnyAiGenerating}
          />
        )}

        {/* ── FOOTER STEP NAVIGATION ── */}
        <WizardFooter
          wizardStep={wizardStep}
          onPrevStep={() => {
            if (isBlockingNavigation) return;
            setWizardStep((prev) => Math.max(1, prev - 1));
          }}
          onNextStep={handleNextStep}
          onCancel={handleBackToCatalog}
          isEditMode={isExistingCampaign}
          isEditing={isEditing}
          canDirectApprove={canDirectApprove}
          isSaving={isSaving}
          isDisabled={isBlockingNavigation}
          campaignStatus={initialCampaign?.status}
          onSaveChanges={
            isEditing
              ? canDirectApprove
                ? () => handleFinalize('SAVE_CHANGES')
                : () => setIsMemberChangeRequestModalOpen(true)
              : null
          }
        />
      </div>

      {/* ── MODAL: IMPORT FROM CRM LEAD PIPELINE ── */}
      <ImportPipelineLeadsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        modalSearch={modalSearch}
        setModalSearch={setModalSearch}
        modalStageFilter={modalStageFilter}
        setModalStageFilter={setModalStageFilter}
        modalScoreFilter={modalScoreFilter}
        setModalScoreFilter={setModalScoreFilter}
        onFetchPipelineLeads={fetchPipelineLeads}
        isLoadingPipelineLeads={isLoadingPipelineLeads}
        filteredModalLeads={filteredModalLeads}
        selectedLeadIds={selectedLeadIds}
        onToggleSelectAll={handleToggleSelectAll}
        onToggleSelectLead={handleToggleSelectLead}
        formData={formData}
        onConfirmImport={handleConfirmImport}
      />

      {/* ── MODAL: AI GENERATE VS BLANK FORM CONFIRMATION (TAB 2 -> TAB 3) ── */}
      <AiGeneratePromptModal
        isOpen={isAiGeneratePromptModalOpen}
        onClose={handleCloseAiGenerateModal}
        onConfirmAiGenerate={handleConfirmAiGenerateModal}
        onConfirmBlankForm={handleConfirmBlankFormModal}
        targetPersonas={formData.targetPersonas}
        importedLeads={formData.importedLeads}
        importedLeadRoles={importedLeadRoles}
        matrix={formData.matrix}
        isRoleMismatch={Boolean(
          formData.matrix &&
          formData.matrix.length > 0 &&
          (formData.importedLeads?.length > 0 || formData.targetPersonas?.length > 0) &&
          checkRolesOutOfSync(getRolesToUse(), formData.matrix)
        )}
      />

      {/* ── MODAL: AI GENERATION IN-PROGRESS INTERRUPTION WARNING ── */}
      <AiInterruptWarningModal
        isOpen={isAiInterruptWarningModalOpen}
        onClose={() => setIsAiInterruptWarningModalOpen(false)}
        onConfirmLeave={() => {
          setIsAiInterruptWarningModalOpen(false);
          onCancel();
        }}
      />

      {/* ── MODAL: UNSAVED CHANGES EXIT REMINDER ── */}
      <UnsavedChangesReminderModal
        isOpen={isUnsavedChangesModalOpen}
        onClose={() => setIsUnsavedChangesModalOpen(false)}
        onConfirmDiscard={handleConfirmDiscardAndExit}
        onConfirmSave={handleConfirmSaveAndExit}
        canDirectApprove={canDirectApprove}
        isSaving={isSaving}
      />

      {/* ── MODAL: REQUEST CAMPAIGN REVISIONS (MANAGER REVIEW) ── */}
      <RequestChangesModal
        isOpen={isRequestChangesModalOpen}
        onClose={() => setIsRequestChangesModalOpen(false)}
        campaign={initialCampaign}
        currentUserName={currentUserName}
        userRoleLabel={userRoleLabel}
        onSubmitRevisionRequest={handleConfirmRevisionRequest}
      />

      {/* ── MODAL: MEMBER SUBMIT CAMPAIGN CHANGE REQUEST ── */}
      <MemberSubmitChangeRequestModal
        isOpen={isMemberChangeRequestModalOpen}
        onClose={() => setIsMemberChangeRequestModalOpen(false)}
        campaign={initialCampaign}
        formData={formData}
        currentUserName={currentUserName}
        userRoleLabel={userRoleLabel}
        onSubmitChangeRequest={handleConfirmMemberChangeRequest}
      />

      {/* ── MODAL: EXECUTIVE SIGN CONTRACT & RE-CERTIFICATION ── */}
      <ExecutiveSignContractModal
        isOpen={isExecutiveSignModalOpen}
        onClose={() => setIsExecutiveSignModalOpen(false)}
        campaign={initialCampaign}
        formData={formData}
        approvedGates={approvedGates}
        currentUserName={currentUserName}
        userRoleLabel={userRoleLabel}
        workspaceName={workspaceName}
        onConfirmApproval={handleConfirmExecutiveApproval}
      />

      {/* ── MODAL: CAMPAIGN OUTREACH LAUNCHER (STEP 1 TOUCHPOINT) ── */}
      <CampaignOutreachLauncherModal
        isOpen={outreachLauncherState.isOpen}
        onClose={(deliveryMap) => {
          const wasPostCreation = Boolean(outreachLauncherState.isPostCreation);
          const currentTouchpoint = outreachLauncherState.touchpoint;
          const currentCampaign = outreachLauncherState.campaign;
          const activeCampaignId = currentCampaign?.id || formData?.id;

          if (deliveryMap && currentTouchpoint) {
            handleUpdateTouchpointDispatched(currentTouchpoint, deliveryMap);
          }

          setOutreachLauncherState((prev) => ({ ...prev, isOpen: false, isPostCreation: false }));

          if (wasPostCreation) {
            // Immediately navigate away so the user is never stuck on Step 6
            if (typeof onCancel === 'function') {
              onCancel();
            }

            // Perform background refresh in fire-and-forget mode without blocking navigation
            if (activeCampaignId) {
              axios
                .get(`/campaign/${activeCampaignId}`)
                .then((res) => {
                  if (res.data?.success && res.data.data) {
                    if (typeof onCampaignDispatchedUpdate === 'function') {
                      onCampaignDispatchedUpdate(res.data.data);
                    }
                  }
                })
                .catch((err) => {
                  console.error('[CreateCampaign] Error refreshing campaign on modal close:', err);
                });
            }
          } else {
            setWizardStep(4);
          }
        }}
        onUpdateDispatchedLeads={(tp, deliveryMap) => {
          handleUpdateTouchpointDispatched(tp, deliveryMap);
        }}
        channel={outreachLauncherState.channel}
        touchpoint={outreachLauncherState.touchpoint}
        campaign={outreachLauncherState.campaign}
        recipients={outreachLauncherState.recipients}
      />
    </div>
  );
}
